<template>
  <v-container fluid class="pa-0 h-100">
    <v-toolbar flat>
      <v-toolbar-title style="align-items: center; display: flex">
        <v-icon left> mdi-view-grid </v-icon>{{ $t('panelPlanning.pageTitle') }}
        <PanelPlanningHelpDialog />
      </v-toolbar-title>
      <v-divider class="mx-4" inset vertical />

      <!-- Site List 토글 버튼 -->
      <v-btn icon @click="siteListDrawer = !siteListDrawer">
        <v-icon>mdi-menu</v-icon>
      </v-btn>

      <!-- 🧭 정렬 가이드 (중앙) -->
      <span
        class="align-guide-inline text-caption text--secondary d-none d-md-inline-block mx-auto"
      >
        <v-icon x-small class="mr-1"> mdi-compass-rose </v-icon>
        {{ $t('panelPlanning.alignGuide') }}
      </span>

      <v-spacer />

      <!-- Tree 구조 토글 버튼 -->
      <v-btn icon @click="treeDrawer = !treeDrawer">
        <v-icon>mdi-file-tree</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- 현재 선택 Array · String · Module (sticky) -->
    <v-sheet
      v-if="selectedSite"
      tag="div"
      class="hierarchy-selection-bar d-flex align-center flex-wrap px-3 py-1"
      :class="{
        'hierarchy-selection-bar--unset': !hasStringDrawTargetArray && !isStringPlacementPreviewActive,
        'hierarchy-selection-bar--preview': isStringPlacementPreviewActive
      }"
      elevation="0"
    >
      <span class="text-caption text--secondary mr-2 flex-shrink-0">
        {{ $t('panelPlanning.hierarchySelectionTitle') }}
      </span>
      <v-chip
        small
        label
        outlined
        class="mr-1 mb-1 hierarchy-selection-bar__chip"
        :color="hierarchySelectionDisplay.arrayLabel ? 'primary' : 'grey lighten-1'"
      >
        <span class="text--secondary mr-1">{{ $t('panelPlanning.treeTypeArray') }}</span>
        {{ hierarchySelectionDisplay.arrayLabel || $t('panelPlanning.hierarchySelectionEmpty') }}
      </v-chip>
      <v-icon small class="mx-1 mb-1 flex-shrink-0 text--disabled">mdi-chevron-right</v-icon>
      <v-chip
        small
        label
        outlined
        class="mr-1 mb-1 hierarchy-selection-bar__chip"
        :color="hierarchySelectionDisplay.stringLabel ? 'primary' : 'grey lighten-1'"
      >
        <span class="text--secondary mr-1">{{ $t('panelPlanning.treeTypeString') }}</span>
        {{ hierarchySelectionDisplay.stringLabel || $t('panelPlanning.hierarchySelectionEmpty') }}
      </v-chip>
      <v-icon small class="mx-1 mb-1 flex-shrink-0 text--disabled">mdi-chevron-right</v-icon>
      <v-chip
        small
        label
        outlined
        class="mr-2 mb-1 hierarchy-selection-bar__chip"
        :color="hierarchySelectionDisplay.moduleLabel ? 'primary' : 'grey lighten-1'"
      >
        <span class="text--secondary mr-1">{{ $t('panelPlanning.treeTypeModule') }}</span>
        {{ hierarchySelectionDisplay.moduleLabel || $t('panelPlanning.hierarchySelectionEmpty') }}
      </v-chip>
      <v-spacer class="d-none d-md-flex" />
      <v-chip
        v-if="isStringPlacementPreviewActive"
        small
        label
        outlined
        color="success"
        class="mb-1 hierarchy-selection-bar__preview-chip"
      >
        <v-icon x-small left color="success">mdi-target-variant</v-icon>
        {{ $t('panelPlanning.stringPlacementPreview', { name: stringPlacementPreviewLabel }) }}
      </v-chip>
      <span
        v-else-if="hasStringDrawTargetArray"
        class="text-caption primary--text mb-1 flex-shrink-0"
      >
        <v-icon x-small color="primary" class="mr-1">mdi-draw</v-icon>
        {{ $t('panelPlanning.stringDrawTargetArray', { name: stringDrawTargetArrayLabel }) }}
      </span>
      <span v-else class="text-caption warning--text mb-1 flex-shrink-0">
        <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
        {{ $t('panelPlanning.stringDrawTargetArrayUnset') }}
      </span>
    </v-sheet>

    <div class="page-content">
      <!-- Main Content -->
      <div class="main-content">
        <!-- Site List Sidebar -->
        <div
          v-show="siteListDrawer"
          ref="siteSidebar"
          class="site-sidebar"
          :style="{ width: `${siteSidebarWidth}px` }"
        >
          <v-card flat class="d-flex flex-column fill-height">
            <!-- 🔍 Sticky 사이트 검색 -->
            <div class="site-search-sticky px-3 pt-3 pb-2">
              <v-text-field
                ref="siteSearchInput"
                v-model="siteSearchQuery"
                :label="$t('panelPlanning.searchSite')"
                :hint="siteListKeyboardHint"
                :persistent-hint="Boolean(siteListKeyboardHint)"
                prepend-inner-icon="mdi-magnify"
                clearable
                outlined
                dense
                hide-details="auto"
                class="site-search-input"
                @keydown="onSiteSearchKeydown"
              />
            </div>
            <v-divider />
            <v-list dense class="site-list-section">
              <v-subheader>
                <div class="ma-1 font-weight-bold">
                  <v-icon left> mdi-map-marker </v-icon>
                  {{ $t('panelPlanning.siteListHeading') }}
                </div>
              </v-subheader>
              <v-list-item
                v-for="(site, index) in filteredSites"
                :key="site.id"
                :ref="'siteListItem_' + index"
                :class="{
                  'site-list-item--selected': selectedSite?.id === site.id,
                  'site-list-item--keyboard-focus': siteListFocusIndex === index
                }"
                class="site-list-item"
                @click="onSiteListItemClick(site, index)"
              >
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ site.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="site.address" class="text-caption">
                    {{ site.address }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="selectedSite?.id === site.id">
                  <v-icon color="secondary"> mdi-check </v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </div>
        <!-- Map Section -->
        <div
          class="map-container flex-grow-1"
          :style="{ '--panel-module-tree-width': `${moduleTreePanelWidth}px` }"
        >
          <div id="map" class="map-area" />

          <v-overlay
            :value="showMapLoadingOverlay"
            absolute
            z-index="1200"
            class="delete-loading-overlay"
          >
            <v-progress-circular indeterminate size="64" color="primary" />
          </v-overlay>

          <!-- 그리기 도구: 대시보드 지도 툴바와 동일(아이콘 위·이름 아래 스택) -->
          <div
            class="drawing-toolbar drawing-toolbar--map-style"
            :class="{ 'tree-open': treeDrawer }"
          >
            <v-sheet color="surface" elevation="1" class="drawing-toolbar-sheet rounded">
              <div class="d-flex align-center flex-wrap pa-1">
                <span
                  class="drawing-toolbar-heading text-caption mr-2 d-none d-sm-flex align-center"
                >
                  <v-icon small class="mr-1" :color="mapToolbarToggleIconIdle"> mdi-draw </v-icon>
                  {{ $t('panelPlanning.drawToolbarTitle') }}
                </span>
                <MapToolbarTooltipBlock
                  caption-spacing="relaxed"
                  icon-area-size="sm"
                  caption-i18n-key="panelPlanning.drawRectangle"
                  tooltip-i18n-key="panelPlanning.drawRectangleTitle"
                  block-class="toolbarmap_color"
                  :btn-class="mapToolbarToggleBtnClass(drawingToolbar.activeTool === 'rectangle')"
                  :btn-props="{
                    small: true,
                    color: 'surface',
                    elevation: 1,
                    style: rectangleToolBtnStyle
                  }"
                  @click.stop="startDrawingMode('rectangle')"
                >
                  <v-icon
                    :color="mapToolbarToggleIconColor(drawingToolbar.activeTool === 'rectangle')"
                  >
                    mdi-rectangle-outline
                  </v-icon>
                </MapToolbarTooltipBlock>
                <MapToolbarTooltipBlock
                  caption-spacing="relaxed"
                  icon-area-size="sm"
                  caption-i18n-key="panelPlanning.drawPolygon"
                  tooltip-i18n-key="panelPlanning.drawPolygonTitle"
                  block-class="toolbarmap_color"
                  :btn-class="mapToolbarToggleBtnClass(drawingToolbar.activeTool === 'polygon')"
                  :btn-props="{
                    small: true,
                    color: 'surface',
                    elevation: 1,
                    style: polygonToolBtnStyle
                  }"
                  @click.stop="startDrawingMode('polygon')"
                >
                  <v-icon
                    :color="mapToolbarToggleIconColor(drawingToolbar.activeTool === 'polygon')"
                  >
                    mdi-shape-polygon-plus
                  </v-icon>
                </MapToolbarTooltipBlock>
                <MapToolbarTooltipBlock
                  v-if="drawingToolbar.activeTool"
                  caption-spacing="relaxed"
                  icon-area-size="sm"
                  caption-i18n-key="panelPlanning.drawExit"
                  tooltip-i18n-key="panelPlanning.drawExitTitle"
                  block-class="toolbarmap_color"
                  :btn-props="{
                    small: true,
                    color: 'surface',
                    elevation: 1,
                    style: { backgroundColor: 'transparent' }
                  }"
                  @click.stop="exitDrawingMode"
                >
                  <v-icon color="error"> mdi-stop </v-icon>
                </MapToolbarTooltipBlock>
                <MapToggleBtn
                  :caption="$t('panelPlanning.autoDrawOn')"
                  :caption-inactive="$t('panelPlanning.autoDrawOff')"
                  :tooltip="$t('panelPlanning.autoDrawTitleOn')"
                  :tooltip-inactive="$t('panelPlanning.autoDrawTitle')"
                  :active="autoDrawEnabled"
                  active-variant="primary"
                  icon="mdi-draw-pen"
                  @toggle="toggleAutoDraw"
                />
              </div>
            </v-sheet>
          </div>

          <!-- Tree 구조 Panel (맵 내부 오른쪽) -->
          <div
            v-show="treeDrawer"
            ref="moduleTreePanel"
            class="tree-panel"
            :style="{ width: `${moduleTreePanelWidth}px` }"
          >
            <v-card flat class="tree-card">
              <!-- Array 추가 헤더 -->
              <div class="tree-header">
                <v-toolbar flat dense>
                  <v-toolbar-title class="text-subtitle-2">
                    <v-icon left small> mdi-file-tree </v-icon>
                    {{ $t('panelPlanning.moduleTree') }}
                  </v-toolbar-title>
                  <v-spacer />
                  <template v-if="showTreeBulkMoveBar">
                    <span class="text-caption mr-2">
                      {{
                        $t('panelPlanning.treeBulkSelectedCount', {
                          count: treeBulkSelectionSummary.count
                        })
                      }}
                    </span>
                    <v-btn
                      small
                      outlined
                      class="solvit-crud-outline-btn solvit-crud-outline-btn--primary mr-2"
                      :disabled="!treeBulkSelectionSummary.canMove"
                      :title="
                        treeBulkSelectionSummary.canMove
                          ? $t('panelPlanning.moveAction')
                          : treeBulkMoveDisabledHint
                      "
                      @click="openTreeBulkMoveDialog"
                    >
                      <v-icon left small>{{ moveActionIcon }}</v-icon>
                      {{ $t('panelPlanning.moveAction') }}
                    </v-btn>
                  </template>
                  <v-btn
                    small
                    outlined
                    class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                    :title="$t('panelPlanning.addArrayTitle')"
                    :loading="isAddingArray"
                    :disabled="isAddingArray"
                    @click="addArrayWithoutModal"
                  >
                    <v-icon left small> mdi-plus-circle </v-icon>
                    {{ $t('panelPlanning.addArray') }}
                  </v-btn>
                </v-toolbar>
              </div>

              <div class="tree-content">
                <RailListSkeleton
                  v-if="showModuleTreeSkeleton"
                  variant="text-row"
                  :count="10"
                  :row-height="moduleTreeRowHeight"
                  root-class="module-tree-skeleton"
                />
                <PanelPlanningVirtualTree
                  v-else
                  ref="moduleVirtualTree"
                  :key="treeRefreshKey"
                  :tree-data="treeData"
                  :tree-revision="panelTreeRevision"
                  :panel-visible="treeDrawer"
                  :focused-node-id="focusedNodeId"
                  :selection-ui-version="selectionUiVersion"
                  :selected-site="selectedSite"
                  :is-selected-fn="isTreeNodeSelected"
                  @node-moved="handleNodeMoved"
                  @name-changed="onTreeNameChanged"
                  @show-message="handleShowMessage"
                  @rename-requested="handleRenameRequested"
                  @move-requested="handleMoveRequested"
                  @delete-requested="handleDeleteRequested"
                  @node-selected="handleTreeNodeSelected"
                  @node-focused="handleTreeNodeFocused"
                  @node-blurred="handleTreeNodeBlurred"
                  @keyboard-navigate="handleKeyboardNavigate"
                  @hydrate-string-modules="hydrateStringTreeModules"
                />
              </div>
            </v-card>
          </div>
        </div>
      </div>
    </div>
    <!-- 🆕 3단계: TreeNode 컨텍스트 메뉴 -->
    <v-menu
      v-model="contextMenu.show"
      :position-x="contextMenu.x"
      :position-y="contextMenu.y"
      absolute
      offset-y
    >
      <v-list dense>
        <!-- 삭제 (가장 자주 사용) -->
        <v-list-item
          v-if="contextMenu.item && contextMenu.item.type === 'string'"
          class="error--text"
          @click="confirmDelete"
        >
          <v-list-item-icon>
            <v-icon small color="error"> mdi-delete </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="error--text">
              {{ $t('panelPlanning.menuDelete') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="contextMenu.item && contextMenu.item.type === 'module'"
          class="error--text"
          @click="confirmDelete"
        >
          <v-list-item-icon>
            <v-icon small color="error"> mdi-delete </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="error--text">
              {{ $t('panelPlanning.menuDeleteModule') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider
          v-if="contextMenu.item && ['array', 'string', 'module'].includes(contextMenu.item.type)"
        />

        <!-- 이름 변경 -->
        <v-list-item
          v-if="contextMenu.item && ['array', 'string', 'module'].includes(contextMenu.item.type)"
          @click="showRenameDialog"
        >
          <v-list-item-icon>
            <v-icon small> mdi-pencil </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('panelPlanning.menuRename') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- String 이동 -->
        <v-list-item
          v-if="contextMenu.item && contextMenu.item.type === 'string'"
          @click="showMoveDialog"
        >
          <v-list-item-icon>
            <v-icon small>{{ moveActionIcon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('panelPlanning.menuMoveArray') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Module 이동 -->
        <v-list-item
          v-if="contextMenu.item && contextMenu.item.type === 'module'"
          @click="showMoveDialog"
        >
          <v-list-item-icon>
            <v-icon small>{{ moveActionIcon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('panelPlanning.menuMoveString') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- 🆕 3단계: 이름 변경 다이얼로그 -->
    <v-dialog
      v-model="renameDialog.show"
      :max-width="modalLayout.XS"
      v-bind="modalPolicy.P1_DISMISSIBLE"
    >
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left> mdi-pencil </v-icon>
          {{
            $t('panelPlanning.renameTitle', {
              type: renameDialog.item
                ? renameDialog.item.type === 'array'
                  ? 'Array'
                  : 'String'
                : ''
            })
          }}
        </v-card-title>

        <v-card-text>
          <v-text-field
            ref="renameNameInput"
            v-model="renameDialog.newName"
            :label="
              $t('panelPlanning.renameField', {
                type: renameDialog.item
                  ? renameDialog.item.type === 'array'
                    ? 'Array'
                    : 'String'
                  : ''
              })
            "
            outlined
            dense
            hide-details
            autofocus
            @keyup.enter="confirmRename"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn outlined class="solvit-crud-outline-btn" @click="renameDialog.show = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            :disabled="!renameDialog.newName || renameDialog.newName.trim() === ''"
            @click="confirmRename"
          >
            {{ $t('common.edit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- String / Module 이동 다이얼로그 -->
    <v-dialog
      v-model="moveDialog.show"
      :max-width="modalLayout.XS"
      v-bind="modalPolicy.P1_DISMISSIBLE"
    >
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left>{{ moveActionIcon }}</v-icon>
          {{
            moveDialog.item?.type === 'module'
              ? moveDialogModuleCount > 1
                ? $t('panelPlanning.moveModuleTitleMultiple', { count: moveDialogModuleCount })
                : $t('panelPlanning.moveModuleTitle')
              : moveDialogStringCount > 1
                ? $t('panelPlanning.moveTitleMultiple', { count: moveDialogStringCount })
                : $t('panelPlanning.moveTitle')
          }}
        </v-card-title>

        <v-card-text>
          <p class="mb-3">
            <template v-if="moveDialog.item?.type === 'module'">
              <template v-if="moveDialogModuleCount > 1">
                {{
                  $t('panelPlanning.moveModuleDescriptionMultiple', {
                    count: moveDialogModuleCount
                  })
                }}
              </template>
              <template v-else>
                {{
                  $t('panelPlanning.moveModuleDescription', {
                    name: moveDialog.item ? moveDialog.item.name : ''
                  })
                }}
              </template>
            </template>
            <template v-else-if="moveDialogStringCount > 1">
              {{ $t('panelPlanning.moveDescriptionMultiple', { count: moveDialogStringCount }) }}
            </template>
            <template v-else>
              {{
                $t('panelPlanning.moveDescription', {
                  name: moveDialog.item ? moveDialog.item.name : ''
                })
              }}
            </template>
          </p>

          <div
            v-if="moveDialog.item?.type === 'string' && moveDialogStringCount > 1"
            class="d-flex flex-wrap mb-3"
          >
            <v-chip
              v-for="stringItem in moveDialog.stringItems"
              :key="stringItem.id"
              small
              outlined
              class="mr-1 mb-1"
            >
              {{ stringItem.name }}
            </v-chip>
          </div>

          <div
            v-if="moveDialog.item?.type === 'module' && moveDialogModuleCount > 1"
            class="d-flex flex-wrap mb-3"
          >
            <v-chip
              v-for="moduleItem in moveDialog.moduleItems"
              :key="moduleItem.id"
              small
              outlined
              class="mr-1 mb-1"
            >
              {{ moduleItem.name }}
            </v-chip>
          </div>

          <v-select
            v-if="moveDialog.item?.type === 'string'"
            v-model="moveDialog.targetArrayId"
            :items="availableArrays"
            item-text="name"
            item-value="id"
            :label="$t('panelPlanning.moveTargetArray')"
            outlined
            dense
            hide-details
          />
          <v-select
            v-else-if="moveDialog.item?.type === 'module'"
            v-model="moveDialog.targetStringId"
            :items="availableStringsForModuleMove"
            item-text="name"
            item-value="id"
            :label="$t('panelPlanning.moveTargetString')"
            outlined
            dense
            hide-details
          />
          <v-checkbox
            v-model="moveDialog.appendAndRenumber"
            :label="$t('panelPlanning.moveAppendAndRenumber')"
            dense
            hide-details
            class="mt-3"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn outlined class="solvit-crud-outline-btn" @click="closeMoveDialog">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            :disabled="!canConfirmMove"
            @click="confirmMove"
          >
            {{ $t('panelPlanning.moveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- OpenLayers Popup -->
    <div id="popup" class="ol-popup">
      <div id="popup-content">
        <v-card flat class="pa-0">
          <v-card-title class="text-h6 pa-2">
            <v-icon left> mdi-grid </v-icon>
            {{ $t('panelPlanning.moduleSplitTitle') }}
          </v-card-title>

          <v-card-text class="pa-3">
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="moduleRowNum"
                  :label="$t('panelPlanning.moduleRowNum')"
                  type="number"
                  min="1"
                  max="50"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="moduleColNum"
                  :label="$t('panelPlanning.moduleColNum')"
                  type="number"
                  min="1"
                  max="50"
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <!-- 🆕 모듈 넘버링 방향 선택 -->
            <v-radio-group
              v-model="moduleNumberingOrientation"
              row
              dense
              class="mt-1 mb-0"
              hide-details
            >
              <v-radio
                :label="$t('panelPlanning.moduleNumberingRowMajor')"
                value="row-major"
                color="primary"
              />
              <v-radio
                :label="$t('panelPlanning.moduleNumberingColMajor')"
                value="col-major"
                color="primary"
              />
            </v-radio-group>

            <v-alert type="info" dense class="mt-2">
              {{
                $t('panelPlanning.splitPreview', {
                  vertical: moduleRowNum,
                  horizontal: moduleColNum,
                  total: moduleRowNum * moduleColNum
                })
              }}
            </v-alert>
          </v-card-text>

          <v-card-actions class="pa-3 pt-0">
            <v-spacer />
            <v-btn outlined class="solvit-crud-outline-btn" @click="cancelDivision">
              {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
              outlined
              class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
              @click="confirmDivision"
            >
              <v-icon left> mdi-grid </v-icon>
              {{ $t('panelPlanning.splitAction') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </div>
  </v-container>
</template>

<script>
  // import Draw from 'ol/interaction/Draw'; // 사용하지 않음
  import { isAppDebugEnabled } from '@/utils/debug/debugEnv';
  import { MODAL_CLOSE_POLICY, MODAL_MAX_WIDTH } from '@/constants/modalLayout';
  import {
    optimizeCanvasPerformance,
    optimizeCanvasPerformanceOnMapRender
  } from '@/utils/canvasOptimizer';
  import {
    resolveSolarArraySeqFromTreeItem,
    resolveSolarModuleDeleteKeyFromTreeItem,
    resolveSolarStringSeqFromTreeItem
  } from '@/features/panel-planning/utils/flattenModuleTreeRows';
  import {
    createPanelMapFeatureIndex,
    getPanelMapFeaturesForString,
    rebuildPanelMapFeatureIndex
  } from '@/features/panel-planning/utils/panelMapFeatureIndex';
  import {
  readStringDisplayNo,
  readStringSeq,
  readArraySeq,
  readModuleSeq,
  readModuleDisplayNo,
  isModuleMapFeatureWithSeq,
  writeStringIdentityOnFeature
} from '@/features/panel-planning/utils/panelMapFeatureProps';
import {
  moduleDomainToTreeFields,
  moduleFromApi,
  readStringDisplayNoFromTreeItem,
  readStringSeqFromTreeItem,
  stringDomainToTreeFields,
  stringFromFlatRecord
} from '@/features/panel-planning/utils/panelHierarchyMappers';
  import {
    createPanelMapFeatureLabelText,
    createPanelMapStringCentroidLabelStyle,
    createPanelPolygonHaloStyles,
    createPanelVertexHandleHaloStyles
  } from '@/features/panel-planning/utils/panelMapHaloStyle';
  import {
    formatPanelMapModuleLabel,
    formatPanelMapStringLabel,
    getPanelMapLabelVisibility
  } from '@/features/panel-planning/utils/panelMapLabelPolicy';
  import { resolveMapContextMenuStringDeleteTargets, resolveMapContextMenuModuleDeleteTarget } from '@/features/panel-planning/utils/panelMapSelection';
  import {
    mapToolbarActiveStyle as buildMapToolbarActiveStyle,
    mapToolbarStackBtnForegroundVar as getMapToolbarStackBtnForegroundVar,
    mapToolbarToggleBtnClass as buildMapToolbarToggleBtnClass,
    mapToolbarToggleIconColor as getMapToolbarToggleIconColor,
    mapToolbarOnPrimaryIconColor as getMapToolbarOnPrimaryIconColor,
    mapToolbarToggleIconIdle as getMapToolbarToggleIconIdle
  } from '@/utils/mapToolbarTheme';
  import { PANEL_MAP_BULK_SELECTION_THRESHOLD } from '@/features/panel-planning/utils/panelTranslateModifyBindings';
  import { PANEL_MAP_STYLE } from '@/features/panel-planning/utils/panelMapStyle';
  import { PANEL_ACTION_ICONS } from '@/features/panel-planning/utils/panelActionIconRegistry';
  import * as turf from '@turf/turf';
  import { getQuadCorners } from '../utils/getModuleCellCorners.js';
  import { getWidthOrHeight as resolveEdgeOrientation } from '../utils/panelEdgeOrientation.js';
  import ContextMenu from 'ol-contextmenu';
  import 'ol-contextmenu/dist/ol-contextmenu.css';
  import { createEmpty, extend as extendExtent, getCenter, isEmpty } from 'ol/extent';
  import Feature from 'ol/Feature';
  import GeoJSON from 'ol/format/GeoJSON';
  import 'ol/ol.css';
  import Overlay from 'ol/Overlay';
  import { mapActions, mapState } from 'vuex';
  import siteDesignApi from '../../../apis/panel/site_design_api.js';
  import { SiteApi } from '../../../apis/site.api';
  import {
    cloneFlattenedSiteDesign,
    flattenSiteDesignData,
    getDataByLevel,
    getDataStatistics
  } from '../../../utils/siteDesignDataFlattener.js';
  import {
    toPanelMapModuleFeatureId,
    toPanelMapStringFeatureId
  } from '../utils/panelMapFeatureId.js';
  import MapToolbarTooltipBlock from '../../../components/map/MapToolbarTooltipBlock.vue';
  import MapToggleBtn from '../../../components/map/MapToggleBtn.vue';
  import PanelPlanningHelpDialog from './PanelPlanningHelpDialog.vue';
  import { DrawingManager } from './DrawingManager.js';
  import { clipSetup } from '../setups/clipSetup.js';
  import { drawSetup } from '../setups/drawSetup.js';
  import { featureSetup } from '../setups/featureSetup.js';
  import { mapSetup } from '../setups/mapSetup.js';
  import { createPanelMapService } from '../services/panelMapService.js';
  import { opsSetup } from '../setups/opsSetup.js';
  import PanelPlanningVirtualTree from './PanelPlanningVirtualTree.vue';
  import { selectSetup } from '../setups/selectSetup.js';
  import { siteSetup } from '../setups/siteSetup.js';
  import { mapInteractionCancelSetup } from '../setups/mapInteractionCancelSetup.js';
  import { treeSetup } from '../setups/treeSetup.js';
  import { SolarEntityManager } from './SolarEntityManager.js';
  import {
    PANEL_MAP_FLY_DURATION_MS,
    PANEL_MAP_PAN_DURATION_MS
  } from '../utils/panelMapMotion.js';
  import {
    panelPerfMark,
    panelPerfMeasure,
    panelPerfSync,
    panelPerfCountSummary
  } from '../utils/panelPlanningPerfLog.js';
  import {
    installPanelPointerMovePerf,
    isPanelPointerMovePerfEnabled,
    panelPointerMovePerfMarkEvent,
    panelPointerMovePerfRecordStyle,
    panelPointerMovePerfBeginHandler,
    panelPointerMovePerfEndHandler
  } from '../utils/panelPointerMovePerfLog.js';
  import {
    installPanelRendererBenchmarkGlobal,
    runPanelRendererCompare
  } from '../utils/panelMapRendererBenchmark.js';
  import { appPerfSinceMyProfile, appPerfScheduleFirstPaint } from '@/utils/appBootstrapPerfLog.js';
  import {
    normalizeEntityRenameName,
    parseDisplayIdFromRename
  } from '../utils/panelEntityNumbering.js';
  import { buildLazyPanelTreeFromFlattened } from '../utils/buildLazyPanelTreeFromFlattened.js';
  import { getPanelTreeModuleChildren } from '../utils/panelTreeModuleChildrenIndex.js';
  import { schedulePanelTreeCommitAfterPaint } from '../utils/schedulePanelTreeCommit.js';
  import {
    hydrateStringTreeModulesInRegistry,
    updatePanelTreeNodeEntityDisplayInPlace,
    setPanelTreeLocalRegistry,
    snapshotStringModulesState,
    restoreStringModulesState
  } from '../utils/panelTreeLocalRegistry.js';
  import {
    applyOptimisticModuleMove,
    createModuleMoveSnapshot,
    rollbackModuleMove
  } from '../utils/panelModuleMoveOptimistic.js';
  import {
    normalizeSolarArrayApiResult,
    patchStringMoveToArrayInPlace
  } from '../utils/panelSiteDesignPatch.js';

  const getThemeColor = (variableName, fallback) => {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(variableName);
    return value?.trim() || fallback;
  };

  const toRgba = (color, opacity) => {
    if (!color) return color;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const fullHex =
        hex.length === 3
          ? hex
              .split('')
              .map(ch => ch + ch)
              .join('')
          : hex;
      const r = parseInt(fullHex.slice(0, 2), 16);
      const g = parseInt(fullHex.slice(2, 4), 16);
      const b = parseInt(fullHex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
  };

  /** Vue data() 밖 Map/Set — 인스턴스 프로퍼티에 두면 HMR/관찰 시 .clear() 깨질 수 있음 */
  const createPanelMapStyleStore = () => ({
    selectionGroupSet: new Set(),
    /** Sparse selection — 선택된 String DB seq (Module 스타일 O(1) 조회) */
    selectedStringSeqs: new Set(),
    selectionModuleSet: new Set(),
    moduleStyleCache: new Map(),
    stringStyleCache: new Map(),
    stringSelectedStyleCache: new Map(),
    moduleSelectedStyleCache: new Map(),
    moduleInStringSelectionStyleCache: new Map()
  });

  const panelMapStyleStores = new WeakMap();

  const clearPanelStyleMapCache = cache => {
    if (cache instanceof Map) {
      cache.clear();
    }
  };

  export default {
    name: 'PanelPlanningPage',
    components: {
      PanelPlanningVirtualTree,
      MapToolbarTooltipBlock,
      MapToggleBtn,
      PanelPlanningHelpDialog,
      RailListSkeleton: () => import('@/components/common/RailListSkeleton.vue')
    },
    mixins: [
      clipSetup,
      selectSetup,
      drawSetup,
      siteSetup,
      mapInteractionCancelSetup,
      treeSetup,
      featureSetup,
      opsSetup
    ],
    data() {
      return {
        modalLayout: MODAL_MAX_WIDTH,
        modalPolicy: MODAL_CLOSE_POLICY,
        // Site List Drawer 관련
        // Tree Drawer 관련
        treeDrawer: true,
        // Array 추가 상태 관리
        isAddingArray: false,
        // 기존 treeData는 store로 이동하므로 제거
        // treeData: [...],

        // 🆕 2단계 리팩토링: SolarEntityManager로 통합 관리

        // 🆕 TreeNode 포커스 상태 관리
        focusedNodeId: null, // 현재 포커스된 노드 ID
        treeBulkSelectedIds: [],
        treeBulkAnchorId: null,

        // 🆕 translate 이벤트 처리를 위한 String 초기 위치 저장
        stringInitialPositions: null,

        autoDrawEnabled: false, // 자동 그리기 모드 (사이트 로드 시 string 유무에 따라 결정)
        isDeleting: false, // String/Module/Array 삭제 중
        isSavingSiteDesign: false, // String 생성·붙여넣기 등 API 저장 중
        /** runWithSiteDesignSaving 중첩 깊이 — isSavingSiteDesign 과 같은 컴포넌트 data 에 둠 */
        siteDesignSaveDepth: 0,

        // 🆕 3단계: 컨텍스트 메뉴 관련 데이터
        contextMenu: {
          show: false,
          x: 0,
          y: 0,
          item: null
        },

        // 🆕 3단계: 이름 변경 다이얼로그
        renameDialog: {
          show: false,
          item: null,
          newName: ''
        },

        // String / Module 이동 다이얼로그
        moveDialog: {
          show: false,
          item: null,
          stringItems: [],
          moduleItems: [],
          targetArrayId: null,
          targetStringId: null,
          appendAndRenumber: false
        },

        // 🆕 DrawingManager 인스턴스
        drawingManager: null,

        // 🆕 그리기 도구 관련 데이터
        drawingToolbar: {
          activeTool: null // 'rectangle', 'polygon'
        },

        // 🆕 평면화된 사이트 디자인 데이터

        map: null,
        view: null,
        vectorSource: null,
        vectorLayer: null,
        drawInteraction: null,
        groupSelection: null, // String 그룹 선택 Collection (Select 대체)
        moduleSelection: null, // Module 단독 선택 Collection (dblclick)
        modifyInteraction: null,
        translateInteraction: null,
        dragPanInteraction: null,
        drawnPolygon: null,
        // ── mapSetup ──
        mapService: null,  // PanelMapService instance (created in beforeCreate)
        mapHost: null,
        mapRenderer: 'canvas',
        lastHighlightedItemId: null, // 마지막 하이라이트된 트리 아이템 ID (토글용)
        modules: [],
        solarStringIndex: 0,
        moduleColNum: 2, // 가로(↔) · API colNum 과 동일
        moduleRowNum: 2, // 세로(↕) · API rowNum 과 동일
        moduleNumberingOrientation: 'row-major', // 'row-major'(가로 기준) | 'col-major'(세로 기준)
        moduleWidth: null,
        moduleHeight: null,
        averageArea: null,
        geoJSON: null,
        selectedGroupFeature: null, // 선택된 그룹(SolarString) 피처 정보
        selectedModuleFeature: null, // 선택된 모듈(SolarModule) 피처 정보
        popup: null, // OpenLayers popup 인스턴스
        mapStyles: {
          defaultStyle: createPanelPolygonHaloStyles(PANEL_MAP_STYLE.string),
          groupSelectedStyle: createPanelPolygonHaloStyles(PANEL_MAP_STYLE.stringSelected),
          moduleSelectedStyle: createPanelPolygonHaloStyles(PANEL_MAP_STYLE.moduleSelected)
        },
        lastMapLabelsKey: null,
        mapLabelZoomBound: false,
        /** 맵 선택 UI(computed) 갱신용 — OL Collection은 반응형 아님 */
        selectionUiVersion: 0,
        treeRefreshKey: 0
      };
    },
    computed: {
      moveActionIcon() {
        return PANEL_ACTION_ICONS.move;
      },

      // Vuex store에서 Tree 데이터 가져오기
      ...mapState('panel/moduleTree', [
        'isCreatingState',
        'isUpdatingState',
        'isDeletingState',
        'errorState',
        'errorMessage',
        'successState',
        'successMessage'
      ]),

      // 로딩 상태 통합
      isLoading() {
        return this.isCreatingState || this.isUpdatingState || this.isDeletingState;
      },

      /** 맵 오버레이 — Array/String/Module 추가·삭제·저장 중 */
      showMapLoadingOverlay() {
        return (
          this.isDeleting ||
          this.isSavingSiteDesign ||
          this.isAddingArray ||
          this.isCreatingState ||
          this.isUpdatingState ||
          this.isDeletingState
        );
      },

      /** 더블클릭으로 Module만 선택된 경우(그룹 선택 없음) */
      canDeleteSelectedModuleFromMap() {
        void this.selectionUiVersion;
        const moduleCount = this.moduleSelection?.getLength() ?? 0;
        const groupCount = this.groupSelection?.getLength() ?? 0;
        return moduleCount > 0 && groupCount === 0;
      },

      // String 이동 다이얼로그 — v-select item-value 는 Array entity PK(seq)
      moveDialogStringCount() {
        if (!this.moveDialog.show || this.moveDialog.item?.type !== 'string') return 0;
        return this.moveDialog.stringItems?.length || (this.moveDialog.item ? 1 : 0);
      },

      moveDialogModuleCount() {
        if (!this.moveDialog.show || this.moveDialog.item?.type !== 'module') return 0;
        return this.moveDialog.moduleItems?.length || (this.moveDialog.item ? 1 : 0);
      },

      availableArrays() {
        if (
          !this.moveDialog.show ||
          this.moveDialog.item?.type !== 'string' ||
          !this.treeData?.length
        ) {
          return [];
        }

        const movingItems =
          this.moveDialog.stringItems?.length > 0
            ? this.moveDialog.stringItems
            : this.moveDialog.item
              ? [this.moveDialog.item]
              : [];
        const movingIds = new Set(movingItems.map(s => String(s.id)));

        return this.treeData
          .filter(item => {
            if (item.type !== 'array') return false;
            const allAlreadyHere = [...movingIds].every(id =>
              item.children?.some(child => String(child.id) === id)
            );
            return !allAlreadyHere;
          })
          .map(array => ({
            id: this.resolveArraySeqFromTreeItem(array),
            name: array.name
          }))
          .filter(entry => entry.id != null && entry.id !== '');
      },

      /** Module 이동 다이얼로그 — 현재 String 제외 전체 String 목록 */
      availableStringsForModuleMove() {
        if (
          !this.moveDialog.show ||
          this.moveDialog.item?.type !== 'module' ||
          !this.treeData?.length
        ) {
          return [];
        }

        const movingModules =
          this.moveDialog.moduleItems?.length > 0
            ? this.moveDialog.moduleItems
            : this.moveDialog.item
              ? [this.moveDialog.item]
              : [];
        const parentIds = new Set(
          movingModules
            .map(moduleItem => this.findParentStringNodeForModule(moduleItem.id)?.id)
            .filter(Boolean)
        );
        const excludeStringId = parentIds.size === 1 ? [...parentIds][0] : null;
        const result = [];

        const walk = (nodes, arrayName = '') => {
          for (const node of nodes || []) {
            if (node.type === 'array') {
              walk(node.children, node.name);
            } else if (node.type === 'string') {
              if (excludeStringId == null || node.id !== excludeStringId) {
                const label = arrayName ? `${arrayName} › ${node.name}` : node.name;
                result.push({
                  id: this.resolveStringSeqFromTreeItem(node),
                  name: label
                });
              }
            } else if (node.children?.length) {
              walk(node.children, arrayName);
            }
          }
        };

        walk(this.treeData);
        return result.filter(entry => entry.id != null && entry.id !== '');
      },

      canConfirmMove() {
        if (!this.moveDialog.item) return false;
        if (this.moveDialog.item.type === 'string') {
          return this.moveDialog.targetArrayId != null && this.moveDialog.targetArrayId !== '';
        }
        if (this.moveDialog.item.type === 'module') {
          return this.moveDialog.targetStringId != null && this.moveDialog.targetStringId !== '';
        }
        return false;
      },

      mapToolbarToggleIconIdle() {
        return getMapToolbarToggleIconIdle(this.$vuetify);
      },
      mapToolbarStackBtnForegroundVar() {
        return getMapToolbarStackBtnForegroundVar(this.$vuetify);
      },
      rectangleToolBtnStyle() {
        return this.mapToolbarActiveStyle(this.drawingToolbar.activeTool === 'rectangle');
      },
      polygonToolBtnStyle() {
        return this.mapToolbarActiveStyle(this.drawingToolbar.activeTool === 'polygon');
      }
    },
    async mounted() {
      panelPerfMark('PanelPlanningPage.mounted');
      appPerfSinceMyProfile('PanelPlanningPage:mounted');
      // ── mapSetup service init ──
      this.mapService = createPanelMapService();
      panelPerfMark('PanelPlanningPage.initMap:start');
      this.initMap('map');
      panelPerfMeasure('PanelPlanningPage.initMap', 'PanelPlanningPage.initMap:start');
      panelPerfMark('PanelPlanningPage.initDrawingManager:start');
      this.initializeMapDrawingManager();
      panelPerfMeasure('PanelPlanningPage.initDrawingManager', 'PanelPlanningPage.initDrawingManager:start');
      panelPerfMark('PanelPlanningPage.initMapPlugins:start');
      this.initPanelMapPlugins();
      panelPerfMeasure('PanelPlanningPage.initMapPlugins', 'PanelPlanningPage.initMapPlugins:start');

      this.$nextTick(() => {
        this.initSiteSidebarResize();
        this.initModuleTreeResize();
      });

      // 키보드 리스너 (Ctrl+C/V, CtrlKey 상태)
      this.setupCopyPasteListeners();

      // 🆕 맵 렌더링 완료 후 Canvas 최적화 적용
      optimizeCanvasPerformanceOnMapRender(this.map, 'map', 'PanelPlanningPage');
      this.setupVectorFeatureStyle();
      this.bindMapLabelZoomRefresh();
      this.geoJSON = new GeoJSON();

      // 컨텍스트 메뉴 초기화
      this.initializeMapContextMenu();

      // popup 초기화
      this.initializeMapClickPopup();

      await this.bootstrapSitesAndDesignOnMount();
      panelPerfMeasure('PanelPlanningPage.bootstrapDone', 'PanelPlanningPage.mounted');
      appPerfSinceMyProfile('PanelPlanningPage:bootstrapDone');
      appPerfScheduleFirstPaint('PanelPlanningPage');

      // bootstrap(selectSiteAndLoadDesign)에서 flattened·트리까지 반영됨
      optimizeCanvasPerformance('map', 'PanelPlanningPage');

      this._pointerMovePerfCleanup = installPanelPointerMovePerf({
        map: this.map,
        viewport: this.map?.getViewport?.(),
        getContext: () => this.buildPointerMovePerfContext()
      });

      if (isAppDebugEnabled()) {
        installPanelRendererBenchmarkGlobal(async (options = {}) =>
          this.runPanelRendererBenchmarkCompare(options)
        );
      }
    },

    beforeDestroy() {
      this._pointerMovePerfCleanup?.();
      this._pointerMovePerfCleanup = null;
      this.clearPanelHierarchyBookmark?.();
      this.mapService?.destroy();
      this.mapService = null;
      this.teardownSiteSidebarResize();
      this.teardownModuleTreeResize();
      this.teardownCopyPasteListeners();
    },

    /** Error Boundary — 하위 컴포넌트 트리 오류 격리 */
    errorCaptured(err, vm, info) {
      console.error('[PanelPlanningPage] errorCaptured:', err?.message, info);
      return false;
    },

    methods: {
      // ── mapSetup methods ──
      initMap(targetId) { return mapSetup.methods.initMap.call(this, targetId); },
      initPanelMapPlugins(list) { return mapSetup.methods.initPanelMapPlugins.call(this, list); },

      mapToolbarActiveStyle(on) {
        return buildMapToolbarActiveStyle(this.$vuetify, on);
      },
      mapToolbarToggleIconColor(isActive) {
        return getMapToolbarToggleIconColor(this.$vuetify, isActive);
      },
      mapToolbarOnPrimaryIconColor(isActive) {
        return getMapToolbarOnPrimaryIconColor(this.$vuetify, isActive);
      },
      mapToolbarToggleBtnClass(isActive) {
        return buildMapToolbarToggleBtnClass(this.$vuetify, isActive);
      },
      getPanelMapFeatureIndex() {
        if (!this._cachedMapFeatureIndex && this.vectorSource) {
          this._cachedMapFeatureIndex = createPanelMapFeatureIndex();
          rebuildPanelMapFeatureIndex(this._cachedMapFeatureIndex, this.vectorSource.getFeatures());
        }
        return (
          this._cachedMapFeatureIndex || {
            byStringSeq: new Map(),
            byArrayId: new Map(),
            byModuleId: new Map(),
            moduleCount: 0
          }
        );
      },

      /**
       * Tree 노드 → API 호출용 entity PK 매핑
       * - string → stringSeq (DB PK)
       * - module → solarModuleSeq (DB PK)
       * - array  → id (tree 노드 ID = array PK)
       */
      resolveRenameApiItemId(item) {
        if (!item) return null;
        if (item.type === 'string') return readStringSeqFromTreeItem(item);
        if (item.type === 'module') return item.solarModuleSeq ?? item.seq;
        if (item.type === 'array') return this.resolveArraySeqFromTreeItem(item);
        return item.id;
      },

      toggleAutoDraw() {
        this.autoDrawEnabled = !this.autoDrawEnabled;
      },

      // Vuex actions 연결
      ...mapActions('panel/moduleTree', [
        'confirmAddArray',
        'handleNodeMoved',
        'handleNameChanged',
        'generateTreeDataFromSite',
        'calculateMaxIds'
      ]),

      /**
       * 🆕 Array 즉시 추가 (모달 없이 바로 실행)
       */
      async addArrayWithoutModal() {
        if (this.isAddingArray) {
          console.log('⚠️ 이미 Array 추가 중입니다.');
          return;
        }

        this.isAddingArray = true;

        try {
          // store action 호출하여 Array 추가
          const result = await this.confirmAddArray({
            siteId: this.selectedSite?.id,
            arrayData: {},
            sectionId: this.siteSectionId
          });

          if (result.success) {
            const resolvedSectionId =
              result.data?._sectionSeq ?? this.siteSectionId;
            if (resolvedSectionId != null && this.siteSectionId == null) {
              this.siteSectionId = resolvedSectionId;
            }

            const ok = this.applyArrayCreatedPatch?.({
              apiResult: result.data,
              requestPayload: {
                name: result.name,
                arrayId: result.arrayDisplayId
              },
              sectionId: resolvedSectionId
            });
            if (!ok) {
              throw new Error('Array seq missing from API response or flattened data not ready');
            }

            const apiDto = normalizeSolarArrayApiResult(result.data);
            this.$emit('show-message', {
              type: 'success',
              text: this.$t('panelPlanning.arrayAddedSuccess', {
                name: apiDto.arrayId ?? result.name
              })
            });
          }
        } catch (error) {
          console.error('❌ Array 추가 실패:', error);
          this.$emit('show-message', {
            type: 'error',
            text: error?.message || this.$t('panelPlanning.arrayAddFailedReload')
          });
        } finally {
          this.isAddingArray = false;
        }
      },

      /**
       * TreeNode에서 발생하는 이동 이벤트 처리 (store action 호출)
       */
      processTreeNodeMoveEvent(data) {
        console.log('🎯 Node 이동 이벤트:', data);

        const { sourceItem, targetItem, sourceGroupName, targetGroupName } = data;

        if (sourceGroupName === 'string' && targetGroupName === 'array') {
          this.handleNodeMoved({ sourceItem, targetItem, sourceGroupName, targetGroupName })
            .then(() => {
              this.moveStringFeaturesToNewArrayOnMap?.(sourceItem, targetItem);
              this.rebuildPanelMapFeatureIndex?.();
            })
            .catch(error => {
              console.error('❌ String Array 이동 실패:', error);
            });
        }
      },

      /**
       * TreeNode·믹스인 show-message → GlobalAlert(commonAlert)
       */
      handleShowMessage(messageData) {
        const text = messageData?.text || messageData?.message || '';
        if (!text) {
          return;
        }
        const type = messageData?.type || 'info';
        this.$store.dispatch('commonAlert/addAlert', {
          type,
          message: text,
          timeout:
            messageData?.timeout ?? (type === 'error' ? 0 : 6000)
        });
      },

      /**
       * 맵에서 String 피처들을 이동 (기존 로직 유지, 향후 store로 이동 예정)
       */
      moveStringFeaturesToNewArrayOnMap(stringItem, targetArray, displayUpdate = null) {
        const stringSeq = readStringSeqFromTreeItem(stringItem) ?? stringItem.id;
        const targetArrayPk =
          this.resolveArraySeqFromTreeItem(targetArray) ?? targetArray.solarArrayId;

        patchStringMoveToArrayInPlace(
          this.flattenedSiteDesign,
          this.solarStrings,
          stringSeq,
          targetArrayPk,
          displayUpdate
        );
        this.syncFlattenedToStore?.({ force: true });

        if (!this.vectorSource) return;

        const allFeatures = this.vectorSource.getFeatures();

        const stringFeatures = allFeatures.filter(
          feature => readStringSeq(feature) === String(stringSeq)
        );

        stringFeatures.forEach(feature => {
          feature.set('arraySeq', targetArrayPk);
          feature.set('solarArrayId', targetArrayPk);
          feature.set('solarArraySeq', targetArrayPk);
          feature.set('solarArrayName', targetArray.name);
          console.log(`🗺️ 맵 피처 업데이트: ${feature.get('name')} → ${targetArray.name}`);
        });

        panelPointerMovePerfMarkEvent('string-move-map-update', {
          stringSeq,
          updatedFeatures: stringFeatures.length,
          targetArraySeq: targetArrayPk,
          targetArrayName: targetArray.name
        });

        this.rebuildPanelMapFeatureIndex?.();
        this.requestVectorStyleRefresh();
      },

      /** Module 피처의 소속 String 메타 갱신 */
      moveModuleFeatureToNewStringOnMap(moduleItem, targetStringItem) {
        if (!this.vectorSource || !moduleItem || !targetStringItem) return;

        const moduleKey = resolveSolarModuleDeleteKeyFromTreeItem(moduleItem, {
          flattenedSiteDesign: this.flattenedSiteDesign
        });
        const targetStringSeq = this.resolveStringSeqFromTreeItem(targetStringItem);
        const targetStringBusinessId =
          readStringDisplayNoFromTreeItem(targetStringItem) ?? targetStringSeq;

        this.vectorSource.getFeatures().forEach(feature => {
          if (feature.get('featureType') !== 'Module') return;
          if (!isModuleMapFeatureWithSeq(feature, moduleKey)) return;

          writeStringIdentityOnFeature(feature, {
            seq: targetStringSeq,
            displayNo: targetStringBusinessId
          });
          feature.set('parentStringName', targetStringItem.name);
        });

        this.requestVectorStyleRefresh();
      },

      findParentStringNodeForModule(moduleNodeId) {
        if (moduleNodeId == null) return null;
        const target = String(moduleNodeId);

        const walk = nodes => {
          for (const node of nodes || []) {
            if (node.type === 'string') {
              if (node.children?.some(child => String(child.id) === target)) {
                return node;
              }
              const found = walk(node.children);
              if (found) return found;
            } else if (node.children?.length) {
              const found = walk(node.children);
              if (found) return found;
            }
          }
          return null;
        };

        return walk(this.treeData);
      },

      /**
       * 지도 클릭 시 표시되는 popup 초기화
       */
      initializeMapClickPopup() {
        const popupElement = document.getElementById('popup');
        if (!popupElement || !this.map) {
          console.warn('initializeMapClickPopup: popup 또는 map 없음');
          return;
        }

        const mapElement = this.map.getTargetElement?.();
        if (mapElement && popupElement.parentElement !== mapElement) {
          mapElement.appendChild(popupElement);
        }

        if (this.popup) {
          this.map.removeOverlay(this.popup);
        }

        this.popup = new Overlay({
          element: popupElement,
          positioning: 'bottom-center',
          stopEvent: true,
          offset: [0, -10]
        });

        // 맵에 popup 추가
        this.map.addOverlay(this.popup);
      },

      /**
       * 지도 우클릭 컨텍스트 메뉴 초기화
       */
      initializeMapContextMenu() {
        try {
          const contextMenu = new ContextMenu({
            width: 250,
            defaultItems: false,
            condition: _event => {
              if (this.drawingManager && this.drawingManager.isCurrentlyDrawing()) {
                console.log('🚫 그리기 중이므로 컨텍스트 메뉴 비활성화');
                return false;
              }
              return true;
            }
          });

          // 메뉴가 열리기 전에 선택 상태에 따라 항목 구성
          contextMenu.on('beforeopen', event => {
            contextMenu.clear();
            contextMenu.extend(this.buildMapContextMenuItems(event.coordinate));
          });

          this.map.addControl(contextMenu);
          this.contextMenu = contextMenu;
        } catch (error) {
          console.error('컨텍스트 메뉴 초기화 중 오류:', error);
        }
      },

      /**
       * 맵 우클릭 컨텍스트 메뉴 항목
       * @see panelMapSelection.js — Module/String 삭제 표시 정책
       */
      buildMapContextMenuItems(coordinate) {
        const longitude = coordinate[0].toFixed(6);
        const latitude = coordinate[1].toFixed(6);
        const coordinateLine = this.$t('panelPlanning.contextMenuCoordinates', {
          lng: longitude,
          lat: latitude
        });

        const items = [];
        const hitFeature = this.getMapFeatureAtCoordinate(coordinate);
        const moduleDeleteTarget = resolveMapContextMenuModuleDeleteTarget({
          hitFeature,
          moduleSelectionFeatures: this.moduleSelection?.getArray() ?? [],
          groupSelectionFeatures: this.groupSelection?.getArray() ?? []
        });

        if (moduleDeleteTarget?.feature) {
          items.push({
            text: this.$t('panelPlanning.menuDeleteSelectedModule'),
            callback: () => {
              this.confirmDeleteModuleFromMap(moduleDeleteTarget.feature);
            }
          });
        } else {
          const deleteTargets = this.resolveMapContextMenuStringDeleteTargets(coordinate);
          if (deleteTargets?.stringSeqs?.length) {
            const count = deleteTargets.stringSeqs.length;
            const menuText =
              count > 1
                ? this.$t('panelPlanning.menuDeleteSelectedStrings', { count })
                : this.$t('panelPlanning.menuDeleteString');
            items.push({
              text: menuText,
              callback: () => {
                this.deleteStringsFromMapContextMenu(deleteTargets.stringSeqs);
              }
            });
          }
        }

        if (items.length) {
          items.push('-');
        }

        items.push(
          {
            text: this.$t('panelPlanning.contextMenuDrawRectangleString'),
            callback: () => {
              if (!this.ensureStringDrawTargetArrayForDrawing()) return;
              this.startRectangleDrawing(true);
            }
          },
          {
            text: this.$t('panelPlanning.contextMenuDrawPolygonString'),
            callback: () => {
              if (!this.ensureStringDrawTargetArrayForDrawing()) return;
              this.startPolygonDrawing(true);
            }
          },
          '-',
          {
            text: coordinateLine,
            callback: () => {
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(`${longitude}, ${latitude}`);
              }
            }
          }
        );

        return items;
      },

      getMapFeatureAtCoordinate(coordinate) {
        if (!this.map) {
          return null;
        }
        const pixel = this.map.getPixelFromCoordinate(coordinate);
        return this.getMapClickTargetFeatureAtPixel(pixel);
      },

      resolveMapContextMenuStringDeleteTargets(coordinate) {
        void this.selectionUiVersion;
        return resolveMapContextMenuStringDeleteTargets({
          hitFeature: this.getMapFeatureAtCoordinate(coordinate),
          groupSelectionFeatures: this.groupSelection?.getArray() ?? [],
          allFeatures: this.vectorSource?.getFeatures() ?? [],
          moduleOnlySelected: this.canDeleteSelectedModuleFromMap
        });
      },

      async deleteStringsFromMapContextMenu(stringSeqs) {
        if (!stringSeqs?.length) {
          this.$emit('show-message', {
            type: 'warning',
            text: this.$t('panelPlanning.alertNoFeatureToDelete')
          });
          return;
        }

        if (stringSeqs.length === 1) {
          await this.deleteSelectedFeature('group', stringSeqs[0]);
          return;
        }

        const confirmMessage = this.$t('panelPlanning.confirmDeleteMultipleStrings', {
          count: stringSeqs.length
        });
        if (!confirm(confirmMessage)) {
          return;
        }

        this.isDeleting = true;
        let successCount = 0;
        const failedNames = [];

        try {
          for (const stringSeq of stringSeqs) {
            const item = this.resolveStringTreeItemForDelete(stringSeq);
            if (!item) {
              failedNames.push(String(stringSeq));
              continue;
            }
            const ok = await this.confirmDeleteStringFromTree(item, { skipConfirm: true });
            if (ok) {
              successCount += 1;
            } else {
              failedNames.push(item.name || String(stringSeq));
            }
          }

          if (successCount > 0) {
            this.clearAllMapSelection?.();
            this.$emit('show-message', {
              type: 'success',
              text: this.$t('panelPlanning.stringsDeletedSuccess', { count: successCount })
            });
          }
          if (failedNames.length > 0) {
            this.$emit('show-message', {
              type: 'error',
              text: this.$t('panelPlanning.stringDeleteFailedMsg', {
                message: failedNames.join(', ')
              })
            });
          }
        } finally {
          this.isDeleting = false;
        }
      },

      /**
       * 맵에서 String 선택 후 삭제 — 트리 삭제 버튼·트리 컨텍스트 메뉴와 동일 경로
       * @param {string} type - 'group' (String 단위)
       * @param {string|number} stringSeq - solarStringSeq (API id)
       */
      async deleteSelectedFeature(type, stringSeq) {
        if (type !== 'group') {
          console.warn('⚠️ 지원하지 않는 삭제 타입:', type);
          return;
        }
        const item = this.resolveStringTreeItemForDelete(stringSeq);
        if (!item) {
          this.$emit('show-message', {
            type: 'error',
            text: this.$t('panelPlanning.alertNoFeatureToDelete')
          });
          return;
        }
        await this.confirmDeleteStringFromTree(item);
      },

      /**
       * 맵에서 Module만 선택된 경우 삭제 — 트리 삭제와 동일 API
       */
      async confirmDeleteModuleFromMap(featureOverride = null) {
        const feature =
          featureOverride?.get
            ? featureOverride
            : (this.canDeleteSelectedModuleFromMap ? this.moduleSelection.getArray()[0] : null);

        if (!feature) {
          this.$emit('show-message', {
            type: 'warning',
            text: this.$t('panelPlanning.alertNoFeatureToDelete')
          });
          return;
        }
        const moduleSeq = readModuleSeq(feature) ?? feature.get('solarModuleSeq');
        if (moduleSeq == null || moduleSeq === '') {
          this.$emit('show-message', {
            type: 'error',
            text: this.$t('panelPlanning.moduleDeleteFailedMsg', {
              message: 'Module seq not found'
            })
          });
          return;
        }
        const displayNo = readModuleDisplayNo(feature);
        const treeItem = this.findModuleNodeByModuleSeq?.(moduleSeq) ?? {
          type: 'module',
          id: `module_${moduleSeq}`,
          name: feature.get('name') || `Module ${displayNo ?? moduleSeq}`,
          solarModuleSeq: moduleSeq,
          seq: moduleSeq,
          moduleId: displayNo,
          solarModuleId: displayNo
        };

        await this.confirmDeleteModuleFromTree(treeItem);
      },

      /**
       * 🆕 String Seq로 해당 String과 모든 Module 피처 찾기
       * @param {string} stringSeq - String의 Seq
       * @returns {Object} String과 Module 피처들을 포함한 객체
       */
      findStringAndModulesByStringSeq(stringSeq) {
        const result = {
          stringFeature: null,
          moduleFeatures: []
        };

        if (!this.vectorSource) {
          console.warn('⚠️ vectorSource가 초기화되지 않음');
          return result;
        }

        const allFeatures = this.vectorSource.getFeatures();

        allFeatures.forEach(feature => {
          const featureStringSeq = readStringSeq(feature);
          const featureType = feature.get('featureType');

          if (featureStringSeq === String(stringSeq)) {
            if (featureType === 'String') {
              result.stringFeature = feature;
            } else if (featureType === 'Module') {
              result.moduleFeatures.push(feature);
            }
          }
        });

        return result;
      },

      /**
       * 🆕 Array Seq로 해당 Array에 속한 모든 String Seq 찾기
       * @param {string} arraySeq - Array의 Seq
       * @returns {Array} String Seq 배열
       */
      findStringSeqsByArraySeq(arraySeq) {
        const stringSeqs = [];

        if (!this.vectorSource) {
          console.warn('⚠️ vectorSource가 초기화되지 않음');
          return stringSeqs;
        }

        const allFeatures = this.vectorSource.getFeatures();

        allFeatures.forEach(feature => {
          const featureArraySeq = feature.get('solarArraySeq');
          const featureStringSeq = readStringSeq(feature);
          const featureType = feature.get('featureType');

          if (featureArraySeq === arraySeq && featureType === 'String') {
            stringSeqs.push(featureStringSeq);
          }
        });

        console.log(`🔍 Array ${arraySeq}에 속한 String Seq들:`, stringSeqs);
        return stringSeqs;
      },

      /**
       * 🆕 Module ID로 Module 피처 찾기
       * @param {string} moduleId - Module의 ID
       * @returns {Object|null} Module 피처 또는 null
       */
      async removeArrayFromTree(arraySeq) {
        try {
          // Tree에서 Array 노드 제거 로직
          // 이 부분은 Tree 구조에 따라 구현 필요
          console.log(`🌳 Tree에서 Array ${arraySeq} 제거`);

          // TODO: Tree 구조에 맞는 Array 제거 로직 구현
          // 예: this.moduleTreeStore.removeArray(arraySeq);
        } catch (error) {
          console.error('❌ Tree에서 Array 제거 중 오류:', error);
          throw error;
        }
      },

      /**
       * 수정/이동 상호작용의 대상 피처들을 업데이트합니다.
       * Translate(이동)만 사용 — 꼭짓점 편집(Modify) 없음.
       * 바인딩 로직은 @/utils/panel/panelTranslateModifyBindings, attach는 MapHost 플러그인.
       */
      updateModifyTranslateFeatures() {
        this.mapService?.updateTranslate({
          groupSelection: this.groupSelection,
          moduleSelection: this.moduleSelection,
          modifyInteraction: this.modifyInteraction,
          translateInteraction: this.translateInteraction
        }, {
          onTranslateStart: (_event, stringFeatures) => {
            const stringInitialPositions = new Map();
            stringFeatures.forEach(stringFeature => {
              const stringGeometry = stringFeature.getGeometry();
              if (stringGeometry) {
                const extent = stringGeometry.getExtent();
                stringInitialPositions.set(stringFeature, {
                  center: [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]
                });
              }
            });
            this.stringInitialPositions = stringInitialPositions;
            this.beginTranslateCancelSnapshot?.(event);
          },
          onTranslating: (event, stringFeatures) => {
            this.handleTranslating(event, stringFeatures);
          },
          onTranslateEnd: (event, stringFeatures) => {
            this.handleTranslateEnd(event, stringFeatures);
            this.stringInitialPositions = null;
            this.clearTranslateCancelSnapshot?.();
          }
        });
      },

      /**
       * 🆕 translate 중 이벤트 처리: String이 움직이면 해당 String의 Module들도 함께 업데이트
       * @param {Object} event - translate 이벤트
       * @param {Array} stringFeatures - 움직인 String 피처들
       */
      handleTranslating(event, stringFeatures) {
        const perf = panelPointerMovePerfBeginHandler('translate-sync-modules');
        if (!stringFeatures || stringFeatures.length === 0) {
          panelPointerMovePerfEndHandler(perf, { skipped: 'no-string-features' });
          return;
        }
        if (!this.stringInitialPositions || this.stringInitialPositions.size === 0) {
          panelPointerMovePerfEndHandler(perf, { skipped: 'no-initial-positions' });
          return;
        }

        try {
          const allFeatures = this.vectorSource.getFeatures();
          const movedFeatures = event.features.getArray();
          let syncedModules = 0;

          // 움직인 피처 중 String만 처리
          movedFeatures.forEach(movedFeature => {
            const featureType = movedFeature.get('featureType');
            if (featureType !== 'String') return; // String만 처리

            const stringSeq = readStringSeq(movedFeature);
            if (stringSeq == null) return;

            // String의 초기 위치와 현재 위치 비교하여 delta 계산
            const initialPos = this.stringInitialPositions.get(movedFeature);
            if (!initialPos) return;

            const stringGeometry = movedFeature.getGeometry();
            if (!stringGeometry) return;

            const currentExtent = stringGeometry.getExtent();
            const currentCenter = [
              (currentExtent[0] + currentExtent[2]) / 2,
              (currentExtent[1] + currentExtent[3]) / 2
            ];

            // delta 계산
            const delta = [
              currentCenter[0] - initialPos.center[0],
              currentCenter[1] - initialPos.center[1]
            ];

            // delta가 0이면 이동하지 않음
            if (Math.abs(delta[0]) < 0.000001 && Math.abs(delta[1]) < 0.000001) return;

            // 초기 위치 업데이트 (다음 프레임을 위해)
            initialPos.center = currentCenter;

            // 해당 String의 모든 Module 찾기 (selfSeq 기준)
            const stringModules = allFeatures.filter(f => {
              const fType = f.get('featureType');
              const fStringSeq = readStringSeq(f);
              return (
                fType === 'Module' &&
                fStringSeq != null &&
                fStringSeq === stringSeq &&
                !movedFeatures.includes(f)
              );
            });

            // 각 Module을 String과 같은 delta만큼 이동
            stringModules.forEach(moduleFeature => {
              const moduleGeometry = moduleFeature.getGeometry();
              if (!moduleGeometry) return;

              // Module을 같은 delta만큼 이동
              moduleGeometry.translate(delta[0], delta[1]);
              syncedModules += 1;
            });
          });
          panelPointerMovePerfEndHandler(perf, {
            allFeatureCount: allFeatures.length,
            syncedModules
          });
        } catch (error) {
          panelPointerMovePerfEndHandler(perf, { error: error?.message });
          console.error('❌ translate 중 이벤트 처리 중 오류:', error);
        }
      },

      /**
       * 🆕 translate 종료 이벤트 처리
       * @param {Object} event - translate 이벤트
       * @param {Array} stringFeatures - 움직인 String 피처들
       */
      handleTranslateEnd(event, stringFeatures) {
        // translate 종료 시 추가 처리 필요 시 여기에 구현
        console.log('✅ translate 종료');
      },

      startDrawing() {
        if (!this.ensureDrawingManagerReady()) {
          console.error('❌ DrawingManager가 초기화되지 않음');
          return;
        }

        // 🆕 그리기 도구가 선택되지 않았으면 기본값(직사각형) 사용
        if (!this.drawingToolbar.activeTool) {
          this.drawingToolbar.activeTool = 'rectangle';
          console.log('⚠️ 그리기 도구가 선택되지 않아 기본값(직사각형) 사용');
        }

        // DrawingManager를 사용하여 그리기 시작
        this.drawingManager.startDrawing(
          // 그리기 시작 콜백
          () => {},
          // 그리기 완료 콜백
          (drawnPolygon, drawingMode) => {
            this.drawnPolygon = drawnPolygon;

            // 🆕 그리기 모드에 따른 처리
            this.handleDrawingCompletion(drawnPolygon, drawingMode);
          },
          // 그리기 취소 콜백
          () => {
            console.log('🔄 모듈 그리기가 취소됨');

            // 🆕 그리기 취소 시 도구 상태 초기화
            this.drawingToolbar.activeTool = null;

            this.$emit('show-message', {
              type: 'info',
              text: this.$t('panelPlanning.moduleDrawCancelled')
            });
          }
        );
      },

      /**
       * 🆕 그리기 완료 처리 (모드별)
       */
      handleDrawingCompletion(drawnPolygon, drawingMode) {
        this.suppressMapSelectionAfterDraw?.();
        // 그리기 모드가 전달되지 않았으면 현재 선택된 도구 사용
        if (!drawingMode) {
          drawingMode = this.drawingToolbar.activeTool;
        }

        switch (drawingMode) {
          case 'rectangle':
            this.handleRectangleDrawing(drawnPolygon);
            break;
          case 'polygon':
            this.handlePolygonDrawing(drawnPolygon);
            break;
          default:
            console.warn(`⚠️ 알 수 없는 그리기 모드: ${drawingMode}`);
            this.handleRectangleDrawing(drawnPolygon); // 기본값으로 처리
        }

        // 🆕 그리기 완료 후 도구 상태 초기화
        this.drawingToolbar.activeTool = null;
      },

      /**
       * 🆕 직사각형 그리기 완료 처리
       */
      handleRectangleDrawing(drawnPolygon) {
        // 기존의 직사각형 처리 로직
        this.processDrawnPolygon(drawnPolygon);
      },

      /**
       * 🆕 다각형 그리기 완료 처리
       */
      handlePolygonDrawing(drawnPolygon) {
        // 다각형 모드의 특별한 처리 로직
        this.processDrawnPolygon(drawnPolygon);
      },

      /**
       * 🆕 그려진 다각형 공통 처리
       */
      processDrawnPolygon(drawnPolygon) {
        this.drawnPolygon = drawnPolygon;

        // 기존 모듈 크기가 있으면 세로·가로 등분 수 자동 계산
        if (this.moduleWidth !== null && this.moduleHeight !== null) {
          const geom = drawnPolygon.getGeometry();
          const coords = geom.getCoordinates()[0].map(coord => {
            const [x, y] = coord;
            return [x, y];
          });
          const quad = getQuadCorners(coords);
          if (quad.length < 4) {
            console.warn('processDrawnPolygon: 꼭짓점 4개 미만');
            this.showDivisionPopup(drawnPolygon);
            return;
          }
          const [arr1, arr2, arr3, _arr4] = quad;

          const firstTwoPointsDirection = resolveEdgeOrientation(arr1, arr2);
          console.log('첫 번째 변 방향:', firstTwoPointsDirection);

          let colNumEstimate = 0;
          let rowNumEstimate = 0;

          if (firstTwoPointsDirection.type === 'width') {
            console.log('첫 번째 변이 가로 — moduleColNum 계산');
            colNumEstimate = firstTwoPointsDirection.distance / this.moduleWidth;

            const secondSide = resolveEdgeOrientation(arr2, arr3);
            rowNumEstimate = secondSide.distance / this.moduleHeight;
          } else {
            console.log('첫 번째 변이 세로 — moduleRowNum 계산');
            rowNumEstimate = firstTwoPointsDirection.distance / this.moduleHeight;

            const secondSide = resolveEdgeOrientation(arr2, arr3);
            colNumEstimate = secondSide.distance / this.moduleWidth;
          }

          this.moduleRowNum = Math.round(rowNumEstimate);
          this.moduleColNum = Math.round(colNumEstimate);

          this.moduleRowNum = Math.max(1, this.moduleRowNum);
          this.moduleColNum = Math.max(1, this.moduleColNum);

          this.moduleRowNum = Math.min(100, this.moduleRowNum);
          this.moduleColNum = Math.min(100, this.moduleColNum);

          console.log(
            `🎯 자동 계산 — moduleColNum: ${this.moduleColNum}, moduleRowNum: ${this.moduleRowNum}`
          );
        } else {
          // 기존 모듈 크기가 없으면 기본값 사용
          console.log('⚠️ 기존 모듈 크기 정보가 없어 기본값(2×2) 사용');
        }

        // 그리기 완료 후 popup 표시
        this.showDivisionPopup(drawnPolygon);
      },
      interpolate(p1, p2, t) {
        return [p1[0] + (p2[0] - p1[0]) * t, p1[1] + (p2[1] - p1[1]) * t];
      },

      getWidthOrHeight(pt1, pt2) {
        return resolveEdgeOrientation(pt1, pt2);
      },

      getCellCorners(arr1, arr2, arr3, arr4, rows, cols, i, j) {
        const [A, B, C, D] = [arr1, arr2, arr3, arr4]; // 꼭짓점 4개

        // 처음 찍은 2개의 점(arr1, arr2)이 가로인지 세로인지 판단
        const firstTwoPointsDirection = this.getWidthOrHeight(arr1, arr2);
        console.log(`getCellCorners 호출 - i:${i}, j:${j}, rows:${rows}, cols:${cols}`);
        console.log('좌표:', { A, B, C, D });
        console.log('첫 번째 변 방향:', firstTwoPointsDirection);

        if (firstTwoPointsDirection.type === 'width') {
          // 첫 번째 변이 가로인 경우 (좌상→우상)
          console.log('첫 번째 변이 가로 - 가로 방향으로 먼저 나누기');

          // 가로 방향 보간 (top: A→B, bottom: D→C)
          const topStart = this.interpolate(A, B, j / cols); // j는 col 인덱스 (가로)
          const topEnd = this.interpolate(A, B, (j + 1) / cols); // j는 col 인덱스 (가로)
          const bottomStart = this.interpolate(D, C, j / cols); // j는 col 인덱스 (가로)
          const bottomEnd = this.interpolate(D, C, (j + 1) / cols); // j는 col 인덱스 (가로)

          // 세로 방향 보간 (topStart→bottomStart, topEnd→bottomEnd)
          const topLeft = this.interpolate(topStart, bottomStart, i / rows); // i는 row 인덱스 (세로)
          const topRight = this.interpolate(topEnd, bottomEnd, i / rows); // i는 row 인덱스 (세로)
          const bottomRight = this.interpolate(topEnd, bottomEnd, (i + 1) / rows); // i는 row 인덱스 (세로)
          const bottomLeft = this.interpolate(topStart, bottomStart, (i + 1) / rows); // i는 row 인덱스 (세로)

          const result = [topLeft, topRight, bottomRight, bottomLeft];
          return result;
        } else {
          // 첫 번째 변이 세로인 경우 (좌상→좌하)
          console.log('첫 번째 변이 세로 - 세로 방향으로 먼저 나누기');

          // 세로 방향 보간 (left: A→D, right: B→C)
          const leftStart = this.interpolate(A, D, i / rows); // i는 row 인덱스 (세로)
          const leftEnd = this.interpolate(A, D, (i + 1) / rows); // i는 row 인덱스 (세로)
          const rightStart = this.interpolate(B, C, i / rows); // i는 row 인덱스 (세로)
          const rightEnd = this.interpolate(B, C, (i + 1) / rows); // i는 row 인덱스 (세로)

          // 가로 방향 보간 (leftStart→rightStart, leftEnd→rightEnd)
          const topLeft = this.interpolate(leftStart, rightStart, j / cols); // j는 col 인덱스 (가로)
          const topRight = this.interpolate(leftEnd, rightEnd, j / cols); // j는 col 인덱스 (가로)
          const bottomRight = this.interpolate(leftEnd, rightEnd, (j + 1) / cols); // j는 col 인덱스 (가로)
          const bottomLeft = this.interpolate(leftStart, rightStart, (j + 1) / cols); // j는 col 인덱스 (가로)

          const result = [topLeft, topRight, bottomRight, bottomLeft];
          return result;
        }
      },

      async fetchSitesFromServer() {
        const params = {
          size: 1000,
          page: 0,
          sort: 'name,asc',
          keyword: ''
        };
        const response = await SiteApi.getSite(params);
        // API 응답 구조: data.content.content
        this.sites = response?.data?.content?.content || null;
      },

      flyToCoordinates(location, done) {
        const duration = PANEL_MAP_FLY_DURATION_MS;
        const zoom = this.view.getZoom();
        let parts = 2;
        let called = false;
        function callback(complete) {
          --parts;
          if (called) {
            return;
          }
          if (parts === 0 || !complete) {
            called = true;
            done(complete);
          }
        }
        this.view.animate(
          {
            center: location, // Already in EPSG:4326
            duration: duration
          },
          callback
        );
        this.view.animate(
          {
            zoom: zoom - 1,
            duration: duration / 2
          },
          {
            zoom: zoom,
            duration: duration / 2
          },
          callback
        );
      },

      /**
       * 🆕 피처 위치로 지도 이동
       * @param {Feature} feature - 이동할 피처
       * @param {Object} options - 옵션 (padding, duration, maxZoom 등)
       */
      panToFeature(feature, options = {}) {
        if (!feature || !this.map || !this.view) {
          console.warn('⚠️ 피처 또는 지도가 초기화되지 않음');
          return;
        }

        try {
          const geometry = feature.getGeometry();
          if (!geometry) {
            console.warn('⚠️ 피처에 geometry가 없음');
            return;
          }

          const extent = geometry.getExtent();
          if (!extent || extent.length !== 4) {
            console.warn('⚠️ 유효하지 않은 extent');
            return;
          }

          const padding = options.padding || [50, 50, 50, 50]; // 상, 우, 하, 좌
          const duration = options.duration ?? PANEL_MAP_PAN_DURATION_MS;
          const maxZoom = options.maxZoom || 20;

          // 피처의 extent로 지도 이동 (애니메이션)
          this.view.fit(extent, {
            padding: padding,
            duration: duration,
            maxZoom: maxZoom
          });
        } catch (error) {
          console.error('❌ 지도 이동 중 오류:', error);
        }
      },

      /**
       * 여러 피처의 combined extent로 지도 이동 (대량 Array/String 클릭 최적화)
       * 개별 피처를 선택 컬렉션에 추가하지 않고 extent만 계산해 즉시 pan
       * @param {import('ol').Feature[]} features
       * @param {Object} [options]
       */
      panToFeaturesExtent(features, options = {}) {
        if (!features?.length || !this.map || !this.view) return;

        try {
          let extent = null;
          for (const f of features) {
            const geom = f.getGeometry();
            if (!geom) continue;
            if (!extent) {
              extent = geom.getExtent().slice();
            } else {
              extendExtent(extent, geom.getExtent());
            }
          }

          if (!extent) return;

          const padding = options.padding || [50, 50, 50, 50];
          const duration = options.duration ?? PANEL_MAP_PAN_DURATION_MS;
          const maxZoom = options.maxZoom || 20;

          this.view.fit(extent, { padding, duration, maxZoom });
        } catch (error) {
          console.error('❌ extent 기반 pan 중 오류:', error);
        }
      },

      /**
       * Array/String 선택 시 string별 convex hull → MultiPolygon + 레이블
       * - disconnected region 지원 (string마다 별도 polygon)
       * - 타입별 색상 구분 (HIGHLIGHT_STYLE)
       * - 레이블 텍스트 표시
       */
      scheduleVectorStyleRefresh() {
        if (this._styleRefreshFrame != null) {
          cancelAnimationFrame(this._styleRefreshFrame);
        }
        this._styleRefreshFrame = requestAnimationFrame(() => {
          this._styleRefreshFrame = null;
          if (this.vectorLayer) {
            this.vectorLayer.changed();
          } else if (this.map) {
            this.map.render();
          }
        });
      },

      requestVectorStyleRefresh() {
        this.scheduleVectorStyleRefresh();
      },

      getPanelMapStyleStore() {
        let store = panelMapStyleStores.get(this);
        if (!store || !(store.moduleStyleCache instanceof Map)) {
          store = createPanelMapStyleStore();
          panelMapStyleStores.set(this, store);
        }
        return store;
      },

      initMapStylePerformanceCaches() {
        this.getPanelMapStyleStore();
        this._cachedStringDefaultStyle = createPanelPolygonHaloStyles(PANEL_MAP_STYLE.string);
        this._cachedStringVertexHandleStyles = createPanelVertexHandleHaloStyles(
          PANEL_MAP_STYLE.vertexHandle
        );
        this._cachedGroupSelectedModuleUnderStringStyle = createPanelPolygonHaloStyles(
          PANEL_MAP_STYLE.moduleInStringSelection
        );
      },

      clearMapStyleCaches() {
        const store = panelMapStyleStores.get(this);
        if (!store) {
          return;
        }
        clearPanelStyleMapCache(store.moduleStyleCache);
        clearPanelStyleMapCache(store.stringStyleCache);
        clearPanelStyleMapCache(store.stringSelectedStyleCache);
        clearPanelStyleMapCache(store.moduleSelectedStyleCache);
        clearPanelStyleMapCache(store.moduleInStringSelectionStyleCache);
      },

      setupVectorFeatureStyle() {
        this.initMapStylePerformanceCaches();
        this.vectorLayer.setStyle(feature => {
          const trackStyle = isPanelPointerMovePerfEnabled();
          const t0 = trackStyle ? performance.now() : 0;
          const style = this.styleVectorFeature(feature);
          if (trackStyle) {
            panelPointerMovePerfRecordStyle(performance.now() - t0);
          }
          return style;
        });
      },

      buildPointerMovePerfContext() {
        const cached = this._pointerMovePerfCounts;
        const featureCount =
          cached?.featureCount ?? this.vectorSource?.getFeatures?.()?.length ?? 0;
        return {
          featureCount,
          moduleCount: cached?.moduleCount ?? 0,
          stringCount: cached?.stringCount ?? 0,
          isPasteMode: Boolean(this.isPasteMode),
          ctrlDragActive: Boolean(this._mapCtrlDragActive),
          ctrlKeyPressed: Boolean(this.ctrlKeyPressed),
          groupSelection: this.groupSelection?.getLength?.() ?? 0,
          moduleSelection: this.moduleSelection?.getLength?.() ?? 0,
          translateActive: Boolean(this.translateInteraction?.getActive?.())
        };
      },

      refreshPointerMovePerfFeatureCounts() {
        const features = this.vectorSource?.getFeatures?.() ?? [];
        let moduleCount = 0;
        let stringCount = 0;
        features.forEach(feature => {
          const type = (feature.get?.('featureType') || '').toLowerCase();
          if (type === 'module') {
            moduleCount += 1;
          } else if (type === 'string') {
            stringCount += 1;
          }
        });
        this._pointerMovePerfCounts = {
          featureCount: features.length,
          moduleCount,
          stringCount
        };
      },

      /** DevTools: await __panelRendererBenchmark() — Canvas vs WebGL 벡터 draw ms 비교 */
      async runPanelRendererBenchmarkCompare(options = {}) {
        if (!this.map || !this.vectorSource) {
          throw new Error('[panel-benchmark] map not ready — site 로드 후 실행하세요');
        }
        return runPanelRendererCompare({
          map: this.map,
          vectorSource: this.vectorSource,
          styleFn: feature => this.styleVectorFeature(feature),
          ...options
        });
      },

      getStringPolygonSelectedStyles(feature) {
        const { stringSelectedStyleCache, selectedStringSeqs } = this.getPanelMapStyleStore();
        const stringIndex = this.parseStringIndexFromFeatureName(feature.get('name'));
        const { stringFormat, cacheKey: labelKey } = this.getMapLabelVisibility();
        const bulkSelected = selectedStringSeqs.size > PANEL_MAP_BULK_SELECTION_THRESHOLD;
        const cacheKey = `${feature.getId() ?? feature.ol_uid}_${labelKey}_${
          bulkSelected ? 'bulk' : 'full'
        }`;
        let cached = stringSelectedStyleCache.get(cacheKey);
        if (cached) {
          return cached;
        }
        const labelText =
          stringIndex != null ? formatPanelMapStringLabel(stringIndex, stringFormat) : null;
        const haloStyles = createPanelPolygonHaloStyles({
          ...PANEL_MAP_STYLE.stringSelected,
          text: labelText ? createPanelMapFeatureLabelText(labelText) : undefined
        });
        if (bulkSelected) {
          cached = haloStyles;
        } else {
          const coordinates = this.getStringPolygonRingCoordinates(feature);
          const styles = [...haloStyles];
          for (let i = 0; i < coordinates.length; i += 1) {
            styles.push(...this._cachedStringVertexHandleStyles);
          }
          cached = styles;
        }
        stringSelectedStyleCache.set(cacheKey, cached);
        return cached;
      },

      getModuleFeatureCount() {
        return this.getPanelMapFeatureIndex().moduleCount ?? 0;
      },

      getMapLabelVisibility() {
        return getPanelMapLabelVisibility({
          zoom: this.view?.getZoom() ?? 0,
          moduleFeatureCount: this.getModuleFeatureCount()
        });
      },

      getMapLabelsVisibilityKey() {
        return this.getMapLabelVisibility().cacheKey;
      },

      /** 줌/피처 수 변경 시 String·Module 라벨 on↔off — 레이어 style만 갱신 */
      syncModuleLabelVisibilityAfterFeaturesChange() {
        const key = this.getMapLabelsVisibilityKey();
        if (key === this.lastMapLabelsKey) {
          return;
        }
        this.lastMapLabelsKey = key;
        this.clearMapStyleCaches();
        this.clearPerFeatureStylesForLabelledLayers();
        this.requestVectorStyleRefresh();
      },

      /**
       * 생성 시 붙은 feature.setStyle 제거 → vectorLayer.setStyle(줌 연동) 사용
       */
      clearPerFeatureStylesForLabelledLayers() {
        if (!this.vectorSource) {
          return;
        }
        this.vectorSource.getFeatures().forEach(feature => {
          const type = feature.get('featureType');
          const name = feature.get('name') || '';
          if (
            type === 'Module' ||
            type === 'module' ||
            type === 'String' ||
            type === 'string' ||
            name.startsWith('Module') ||
            name.startsWith('String')
          ) {
            feature.setStyle(null);
          }
        });
      },

      bindMapLabelZoomRefresh() {
        if (!this.view || this.mapLabelZoomBound) {
          return;
        }
        this.mapLabelZoomBound = true;
        this.lastMapLabelsKey = this.getMapLabelsVisibilityKey();
        let labelRefreshFrame = null;
        this.view.on('change:resolution', () => {
          if (labelRefreshFrame != null) {
            cancelAnimationFrame(labelRefreshFrame);
          }
          labelRefreshFrame = requestAnimationFrame(() => {
            labelRefreshFrame = null;
            this.syncModuleLabelVisibilityAfterFeaturesChange();
          });
        });
      },

      parseStringIndexFromFeatureName(featureName) {
        if (!featureName?.startsWith('String')) {
          return null;
        }
        const idx = parseInt(featureName.split(' ')[1], 10);
        return Number.isNaN(idx) ? null : idx;
      },

      /** featureType이 'string'|'String' 혼용 (평면화 vs 레거시) */
      isPanelMapStringFeature(feature) {
        const featureType = feature.get('featureType');
        const name = feature.get('name') || '';
        return featureType === 'String' || featureType === 'string' || name.startsWith('String');
      },

      isPanelMapModuleFeature(feature) {
        const featureType = feature.get('featureType');
        const name = feature.get('name') || '';
        return featureType === 'Module' || featureType === 'module' || name.startsWith('Module');
      },

      getStringPolygonRingCoordinates(feature) {
        const geom = feature.getGeometry();
        if (!geom) {
          return [];
        }
        const type = geom.getType();
        if (type === 'Polygon') {
          return geom.getCoordinates()[0] || [];
        }
        if (type === 'MultiPolygon') {
          return geom.getCoordinates()[0]?.[0] || [];
        }
        return [];
      },

      getCachedStringStyle(stringIndex) {
        const { stringStyleCache } = this.getPanelMapStyleStore();
        const { stringFormat, cacheKey: labelKey } = this.getMapLabelVisibility();
        const cacheKey = `${stringIndex}_${labelKey}`;
        let style = stringStyleCache.get(cacheKey);
        if (style) {
          return style;
        }
        const labelText = formatPanelMapStringLabel(stringIndex, stringFormat);
        style = createPanelPolygonHaloStyles({
          ...PANEL_MAP_STYLE.string,
          text: labelText ? createPanelMapFeatureLabelText(labelText) : undefined
        });
        stringStyleCache.set(cacheKey, style);
        return style;
      },

      isStringLabelAnchorModule(feature) {
        const stringSeq = readStringSeq(feature);
        const moduleId = feature?.get?.('solarModuleId')?.toString();
        if (!stringSeq || !moduleId) {
          return false;
        }
        const bucket = this.getPanelMapFeatureIndex?.()?.byStringSeq?.get(stringSeq);
        const modules = bucket?.modules;
        if (!modules?.length) {
          return false;
        }
        let anchor = modules[0];
        let minId = anchor.get('solarModuleId')?.toString();
        for (let i = 1; i < modules.length; i += 1) {
          const mid = modules[i].get('solarModuleId')?.toString();
          if (mid != null && (minId == null || Number(mid) < Number(minId))) {
            minId = mid;
            anchor = modules[i];
          }
        }
        return anchor === feature;
      },

      resolveModuleMapLabelText(moduleIndex, feature, { moduleFormat, stringFormat }) {
        const moduleLabel = formatPanelMapModuleLabel(moduleIndex, moduleFormat);
        if (moduleLabel) {
          return moduleLabel;
        }
        if (!stringFormat || !feature || !this.isStringLabelAnchorModule(feature)) {
          return null;
        }
        const stringIndex = readStringDisplayNo(feature);
        return formatPanelMapStringLabel(stringIndex, stringFormat);
      },

      /** String 소속 Module extent 중심 — String 라벨 배치용 (solarStringSeq 기준) */
      getStringModulesCentroid(stringSeq) {
        if (!stringSeq) {
          return null;
        }
        const features = getPanelMapFeaturesForString(
          this.getPanelMapFeatureIndex?.() || null,
          String(stringSeq)
        );
        const modules = features.filter(f => this.isPanelMapModuleFeature(f));
        if (!modules.length) {
          return null;
        }
        const extent = createEmpty();
        modules.forEach(f => {
          const geom = f.getGeometry();
          if (geom) {
            extendExtent(extent, geom.getExtent());
          }
        });
        return isEmpty(extent) ? null : getCenter(extent);
      },

      buildModulePolygonStyles(polygonStyleConfig, moduleIndex, feature) {
        const { moduleFormat, stringFormat } = this.getMapLabelVisibility();
        const moduleLabel = formatPanelMapModuleLabel(moduleIndex, moduleFormat);
        const stringLabel =
          !moduleLabel && stringFormat && feature && this.isStringLabelAnchorModule(feature)
            ? formatPanelMapStringLabel(readStringDisplayNo(feature), stringFormat)
            : null;
        const labelText = moduleLabel || stringLabel;
        const useStringCentroid = Boolean(stringLabel);
        const styles = createPanelPolygonHaloStyles({
          ...polygonStyleConfig,
          text:
            labelText && !useStringCentroid ? createPanelMapFeatureLabelText(labelText) : undefined
        });
        if (useStringCentroid && labelText) {
          styles.push(
            createPanelMapStringCentroidLabelStyle(labelText, sid =>
              this.getStringModulesCentroid(sid)
            )
          );
        }
        return styles;
      },

      getCachedModuleStyle(moduleIndex, feature) {
        const { moduleStyleCache } = this.getPanelMapStyleStore();
        const { moduleFormat, stringFormat, cacheKey: labelKey } = this.getMapLabelVisibility();
        const isAnchor = feature && this.isStringLabelAnchorModule(feature);
        const cacheKey = `${moduleIndex}_${labelKey}_${isAnchor ? 's' : 'm'}`;
        let style = moduleStyleCache.get(cacheKey);
        if (style) {
          return style;
        }
        style = this.buildModulePolygonStyles(PANEL_MAP_STYLE.module, moduleIndex, feature);
        moduleStyleCache.set(cacheKey, style);
        return style;
      },

      getCachedModuleSelectedStyle(moduleIndex, feature) {
        const { moduleSelectedStyleCache } = this.getPanelMapStyleStore();
        const { moduleFormat, stringFormat, cacheKey: labelKey } = this.getMapLabelVisibility();
        const isAnchor = feature && this.isStringLabelAnchorModule(feature);
        const cacheKey = `${moduleIndex}_${labelKey}_${isAnchor ? 's' : 'm'}`;
        let style = moduleSelectedStyleCache.get(cacheKey);
        if (style) {
          return style;
        }
        style = this.buildModulePolygonStyles(PANEL_MAP_STYLE.moduleSelected, moduleIndex, feature);
        moduleSelectedStyleCache.set(cacheKey, style);
        return style;
      },

      getCachedModuleInStringSelectionStyle(moduleIndex, feature) {
        const { moduleInStringSelectionStyleCache } = this.getPanelMapStyleStore();
        const { moduleFormat, stringFormat, cacheKey: labelKey } = this.getMapLabelVisibility();
        const isAnchor = feature && this.isStringLabelAnchorModule(feature);
        const cacheKey = `${moduleIndex}_${labelKey}_${isAnchor ? 's' : 'm'}`;
        let style = moduleInStringSelectionStyleCache.get(cacheKey);
        if (style) {
          return style;
        }
        style = this.buildModulePolygonStyles(
          PANEL_MAP_STYLE.moduleInStringSelection,
          moduleIndex,
          feature
        );
        moduleInStringSelectionStyleCache.set(cacheKey, style);
        return style;
      },

      styleVectorFeature(feature) {
        // String은 DB shapeGeog 사각형이 모듈 배치와 어긋날 수 있음 — 지도에는 Module만 표시
        if (this.isPanelMapStringFeature(feature)) {
          return [];
        }

        const { selectionGroupSet, selectionModuleSet, selectedStringSeqs } =
          this.getPanelMapStyleStore();
        const moduleStringSeq = readStringSeq(feature);
        const isGroupSelected =
          selectionGroupSet.has(feature) ||
          (moduleStringSeq != null && selectedStringSeqs.has(moduleStringSeq));
        const isModuleSelected = selectionModuleSet.has(feature);
        const featureName = feature.get('name');

        if (isModuleSelected) {
          if (this.isPanelMapModuleFeature(feature)) {
            const moduleId = parseInt(featureName.split(' ')[1], 10);
            if (!Number.isNaN(moduleId)) {
              return this.getCachedModuleSelectedStyle(moduleId, feature);
            }
          }
          return this.mapStyles.moduleSelectedStyle;
        }
        if (isGroupSelected) {
          if (this.isPanelMapModuleFeature(feature)) {
            const moduleId = parseInt(featureName.split(' ')[1], 10);
            if (!Number.isNaN(moduleId)) {
              return this.getCachedModuleInStringSelectionStyle(moduleId, feature);
            }
            return this._cachedGroupSelectedModuleUnderStringStyle;
          }
          return this.mapStyles.groupSelectedStyle;
        }
        if (this.isPanelMapModuleFeature(feature)) {
          const moduleId = parseInt(featureName.split(' ')[1], 10);
          if (!Number.isNaN(moduleId)) {
            return this.getCachedModuleStyle(moduleId, feature);
          }
        }
        return this.mapStyles.defaultStyle;
      },

      stringStyle(stringIndex) {
        return this.getCachedStringStyle(stringIndex);
      },
      moduleStyle(moduleIndex) {
        return this.getCachedModuleStyle(moduleIndex);
      },
      /**
       * flattened → Vuex 동기화 (display id는 moduleTree getter에서 derived)
       * @deprecated load/patch 경로에서 syncFlattenedToStore 호출 — 중복 방지용 no-op
       */
      calculateMaxIdsFromSolarStrings() {},

      calculateMaxIdsFromFlattenedData() {},

      calculateMaxIdsFromSolarStringsLegacy() {},

      async calculateMaxIdsHandler() {},

      /**
       * 실제 사이트 데이터를 Tree 구조로 변환 (store action 호출)
       * 🚀 평면화된 데이터가 있으면 최적화된 방식 사용
       */
      generateTreeDataFromSiteHandler() {
        panelPerfMark('generateTreeDataFromSiteHandler:start', {
          hasFlattened: !!this.flattenedSiteDesign
        });
        if (this.flattenedSiteDesign) {
          this.generateTreeDataFromFlattenedData({ deferCommit: true });
          panelPerfMeasure('generateTreeDataFromSiteHandler', 'generateTreeDataFromSiteHandler:start');
          return;
        }

        this.generateTreeDataFromSite({
          selectedSite: this.selectedSite,
          solarStrings: this.solarStrings
        });
        panelPerfMeasure('generateTreeDataFromSiteHandler:legacy', 'generateTreeDataFromSiteHandler:start');
      },

      /**
       * 🆕 평면화된 데이터를 사용한 Tree 데이터 생성 (최적화)
       * 기존 중첩 루프 대신 Map을 통한 O(1) 접근으로 성능 향상
       */
      generateTreeDataFromFlattenedData(options = {}) {
        const deferCommit = options.deferCommit === true;
        panelPerfMark('generateTree:flattened:start');
        try {
          if (!this.flattenedSiteDesign) {
            console.warn('⚠️ 평면화된 데이터가 없습니다.');
            return;
          }

          let sections;
          let arrays;
          let strings;
          let modules;
          panelPerfSync('generateTree:getDataByLevel', () => {
            sections = getDataByLevel(this.flattenedSiteDesign, 'section');
            arrays = getDataByLevel(this.flattenedSiteDesign, 'array');
            strings = getDataByLevel(this.flattenedSiteDesign, 'string');
            modules = getDataByLevel(this.flattenedSiteDesign, 'module');
          });

          panelPerfCountSummary('generateTree:levelCounts', {
            sections: sections.length,
            arrays: arrays.length,
            strings: strings.length,
            modules: modules.length
          });

          let treeData;
          panelPerfSync('generateTree:buildLazyTreeFromFlattened', () => {
            treeData = buildLazyPanelTreeFromFlattened(sections, arrays, strings, modules);
          });

          const commitTree = () => {
            panelPerfSync('generateTree:commitDerivedTreeData', () => {
              this.commitDerivedTreeData(treeData);
            });
          };

          if (deferCommit) {
            panelPerfMark('generateTree:commitDeferred:scheduled');
            const siteIdAtSchedule = this.selectedSite?.id;
            this.moduleTreeCommitPending = true;
            schedulePanelTreeCommitAfterPaint(() => {
              if (
                siteIdAtSchedule != null &&
                String(this.selectedSite?.id) !== String(siteIdAtSchedule)
              ) {
                this.moduleTreeCommitPending = false;
                panelPerfCountSummary('generateTree:commitDeferred:skip', { reason: 'staleSite' });
                return;
              }
              commitTree();
              this.moduleTreeCommitPending = false;
              panelPerfMeasure('generateTree:commitDeferred', 'generateTree:commitDeferred:scheduled');
            });
          } else {
            commitTree();
          }

          panelPerfMeasure('generateTree:flattened:build', 'generateTree:flattened:start');
        } catch (error) {
          this.moduleTreeCommitPending = false;
          console.error('❌ 평면화된 데이터로 Tree 데이터 생성 실패:', error);
          console.warn('⚠️ 기존 방식으로 폴백합니다.');
          this.generateTreeDataFromSite({
            selectedSite: this.selectedSite,
            solarStrings: this.solarStrings
          });
        }
      },

      /**
       * 평면화 데이터 → 모듈 트리 (UI)
       * Section은 보고서당 1개이므로 트리 루트에 두지 않고, Array → String → Module 만 표시한다.
       */
      buildTreeFromFlattenedData(sections, arrays, strings, modules) {
        const treeData = [];

        const buildArrayNode = (array, arrayIndex) => {
          // array.id = DB seq (PK), array.arrayId = user-facing string identifier
          const displayId = array.arrayId || array.id;
          const arrayNode = {
            name: array.name || `Array ${displayId}`,
            id: `array_${array.id}`,
            type: 'array',
            arrayId: displayId,
            solarArraySeq: array.id,
            solarArrayId: array.id,
            children: []
          };

          const arrayStrings = strings.filter(string => string.parentId === array.id);

          arrayStrings.forEach((string, stringIndex) => {
            const stringDomain = stringFromFlatRecord(string);
            const stringNode = {
              ...stringDomainToTreeFields(stringDomain),
              name:
                string.name ||
                `String ${stringDomain.displayNo || stringIndex + 1}`,
              children: []
            };

            const stringModules = modules.filter(module => module.parentId === string.id);

            stringModules.forEach((module, moduleIndex) => {
              const moduleDomain = moduleFromApi(module);
              stringNode.children.push({
                ...moduleDomainToTreeFields(moduleDomain),
                name: `Module ${moduleDomain.displayNo || moduleIndex + 1}`,
                children: []
              });
            });

            arrayNode.children.push(stringNode);
          });

          return arrayNode;
        };

        const sectionIdSet = new Set((sections || []).map(s => s.id));
        const arraysUnderSection = sectionId =>
          arrays.filter(array => array.parentId === sectionId);

        if (sections?.length) {
          sections.forEach(section => {
            arraysUnderSection(section.id).forEach((array, arrayIndex) => {
              treeData.push(buildArrayNode(array, arrayIndex));
            });
          });
        }

        // Section 메타 없이 Array만 있는 경우(폴백)
        const rootArrays = arrays.filter(array => !sectionIdSet.has(array.parentId));
        rootArrays.forEach((array, arrayIndex) => {
          treeData.push(buildArrayNode(array, arrayIndex));
        });

        return treeData;
      },

      /**
       * 🚀 2단계 리팩토링: SolarEntityManager 초기화 (평면화된 데이터 최적화)
       * 평면화된 데이터가 있으면 최적화된 방식 사용
       */
      initializeEntityManager() {
        try {
          // EntityManager 인스턴스 생성
          this.entityManager = new SolarEntityManager();

          // 🆕 평면화된 데이터가 있으면 최적화된 방식 사용
          if (this.flattenedSiteDesign) {
            this.initializeEntityManagerFromFlattenedData();
          } else {
            // 🆕 기존 방식 (평면화된 데이터가 없을 때)
            this.entityManager.initializeFromSolarStrings(this.solarStrings || []);
          }

          // 이벤트 리스너 등록
          this.setupEntityManagerListeners();
          // 디버깅: Entity 구조 출력
          this.logEntityHierarchy();
        } catch (error) {
          console.error('❌ SolarEntityManager 초기화 실패:', error);
        }
      },

      /**
       * 🆕 평면화된 데이터를 사용한 EntityManager 초기화 (최적화)
       */
      initializeEntityManagerFromFlattenedData() {
        try {
          if (!this.flattenedSiteDesign) {
            console.warn('⚠️ 평면화된 데이터가 없습니다.');
            return;
          }

          panelPerfMark('entityManager:buildStructure:start');
          const entityData = this.buildEntityStructureFromFlattenedData();
          panelPerfMeasure('entityManager:buildStructure', 'entityManager:buildStructure:start', {
            strings: entityData?.strings?.length ?? 0,
            modules: entityData?.modules?.length ?? 0
          });

          panelPerfMark('entityManager:initialize:start');
          if (typeof this.entityManager.initializeFromEntityData === 'function') {
            this.entityManager.initializeFromEntityData(entityData);
          } else {
            this.entityManager.initializeFromSolarStrings(this.solarStrings || []);
          }
          panelPerfMeasure('entityManager:initialize', 'entityManager:initialize:start');
        } catch (error) {
          console.error('❌ 평면화된 데이터로 EntityManager 초기화 실패:', error);
          console.warn('⚠️ 기존 방식으로 폴백합니다.');
          this.entityManager.initializeFromSolarStrings(this.solarStrings || []);
        }
      },

      /**
       * 🆕 평면화된 데이터로부터 Entity 구조 빌드
       */
      buildEntityStructureFromFlattenedData() {
        const entityData = {
          sections: [],
          arrays: [],
          strings: [],
          modules: []
        };

        // 🚀 각 레벨별 데이터 추출 및 구조화
        const sections = getDataByLevel(this.flattenedSiteDesign, 'section');
        const arrays = getDataByLevel(this.flattenedSiteDesign, 'array');
        const strings = getDataByLevel(this.flattenedSiteDesign, 'string');
        const modules = getDataByLevel(this.flattenedSiteDesign, 'module');

        // 🚀 계층 구조에 맞게 데이터 정리
        sections.forEach(section => {
          const sectionData = {
            id: section.id,
            name: section.name,
            type: 'section',
            children: []
          };

          // 해당 Section에 속한 Arrays 찾기
          const sectionArrays = arrays.filter(array => array.parentId === section.id);
          sectionArrays.forEach(array => {
            const arrayData = {
              id: array.id,
              arrayId: array.arrayId,
              name: array.name,
              type: 'array',
              children: []
            };

            // 해당 Array에 속한 Strings 찾기
            const arrayStrings = strings.filter(string => string.parentId === array.id);
            arrayStrings.forEach(string => {
              const stringData = {
                id: string.id,
                name: string.name,
                type: 'string',
                children: []
              };

              // 해당 String에 속한 Modules 찾기
              const stringModules = modules.filter(module => module.parentId === string.id);
              stringModules.forEach(module => {
                const moduleData = {
                  id: module.id,
                  name: module.moduleId,
                  type: 'module'
                };
                stringData.children.push(moduleData);
              });

              arrayData.children.push(stringData);
            });

            sectionData.children.push(arrayData);
          });

          entityData.sections.push(sectionData);
        });

        return entityData;
      },

      /**
       * 🆕 평면화된 데이터를 기존 solarStrings 형식으로 변환
       * 기존 코드와의 호환성을 위해 사용
       */
      convertFlattenedToStringData(stringInfo, arrayInfo, sectionInfo) {
        // 🚀 평면화된 데이터에서 Module 정보 추출
        const moduleData = getDataByLevel(this.flattenedSiteDesign, 'module').filter(
          module => module.parentId === stringInfo.id
        );

        // 🚀 기존 solarStrings 형식으로 변환
        const solarStringData = {
          id: stringInfo.id,
          stringId: stringInfo.stringId,
          name: stringInfo.name,
          rowNum: stringInfo.rowNum,
          colNum: stringInfo.colNum,
          description: stringInfo.description,
          shapeGeog: stringInfo.shapeGeog,
          solarModules: moduleData.map(module => ({
            id: module.id,
            moduleId: module.moduleId,
            shapeGeog: module.shapeGeog,
            centerCoord: module.centerCoord
          })),
          // 🆕 계층 구조 정보 추가
          solarSectionId: sectionInfo?.id,
          solarArrayId: arrayInfo?.id,
          // 🆕 인덱스 정보 추가
          sectionIndex: stringInfo.sectionIndex,
          arrayIndex: stringInfo.arrayIndex,
          stringIndex: stringInfo.stringIndex
        };

        return solarStringData;
      },

      /**
       * 🆕 로컬 데이터 변경사항 추적 함수
       * 외부 유틸리티 함수 대신 컴포넌트 내부에서 구현
       */
      trackDataChangesLocal(oldData, newData) {
        try {
          if (!oldData || !newData) {
            return {
              added: [],
              removed: [],
              modified: [],
              unchanged: []
            };
          }

          const changes = {
            added: [],
            removed: [],
            modified: [],
            unchanged: []
          };

          // 간단한 변경사항 감지 (실제 구현에서는 더 정교한 로직 필요)
          if (oldData.id !== newData.id) {
            changes.modified.push({
              old: oldData,
              new: newData
            });
          } else {
            changes.unchanged.push(newData);
          }

          return changes;
        } catch (error) {
          console.error('❌ 데이터 변경사항 추적 실패:', error);
          return {
            added: [],
            removed: [],
            modified: [],
            unchanged: []
          };
        }
      },

      /**
       * 🆕 데이터 변경사항 추적 및 실시간 업데이트
       * 평면화된 데이터의 변경을 감지하여 UI 자동 동기화
       */
      trackDataChangesAndUpdate(newSiteDesign) {
        try {
          if (!this.flattenedSiteDesign) {
            console.log('⚠️ 기존 평면화된 데이터가 없어 새로 생성합니다.');
            this.flattenedSiteDesign = flattenSiteDesignData(newSiteDesign);
            return;
          }

          // 🚀 데이터 변경사항 추적
          const changes = this.trackDataChangesLocal(this.selectedSite, newSiteDesign);
          console.log('📊 데이터 변경사항:', changes);

          // 🚀 변경사항에 따른 UI 업데이트
          this.handleDataChanges(changes, newSiteDesign);
        } catch (error) {
          console.error('❌ 데이터 변경사항 추적 실패:', error);
          // 오류 발생 시 전체 데이터 새로 로드
        }
      },

      /**
       * 🆕 데이터 변경사항에 따른 UI 업데이트 처리
       */
      handleDataChanges(changes, newSiteDesign) {
        try {
          // 🚀 추가된 항목 처리
          if (changes.added.length > 0) {
            console.log('➕ 추가된 항목:', changes.added.length, '개');
            this.handleAddedItems(changes.added);
          }

          // 🚀 제거된 항목 처리
          if (changes.removed.length > 0) {
            console.log('➖ 제거된 항목:', changes.removed.length, '개');
            this.handleRemovedItems(changes.removed);
          }

          // 🚀 수정된 항목 처리
          if (changes.modified.length > 0) {
            console.log('✏️ 수정된 항목:', changes.modified.length, '개');
            this.handleModifiedItems(changes.modified);
          }

          // 🚀 변경사항이 있으면 평면화된 데이터 업데이트
          if (
            changes.added.length > 0 ||
            changes.removed.length > 0 ||
            changes.modified.length > 0
          ) {
            console.log('🔄 평면화된 데이터 업데이트');
            this.updateFlattenedData(newSiteDesign, { rebuildTree: false });
          }
        } catch (error) {
          console.error('❌ 데이터 변경사항 처리 실패:', error);
        }
      },

      /**
       * 🆕 추가된 항목 처리
       */
      handleAddedItems(addedItems) {
        addedItems.forEach(item => {
          try {
            switch (item.level) {
              case 'string':
                this.addStringToMap(item);
                break;
              case 'module':
                this.addModuleToMap(item);
                break;
              case 'array':
                this.addArrayToMap(item);
                break;
              case 'section':
                this.addSectionToMap(item);
                break;
            }
          } catch (error) {
            console.error('❌ 추가된 항목 처리 실패:', item, error);
          }
        });
      },

      /**
       * 🆕 제거된 항목 처리
       */
      handleRemovedItems(removedItems) {
        removedItems.forEach(item => {
          try {
            switch (item.level) {
              case 'string':
                this.removeStringFromMap(item);
                break;
              case 'module':
                this.removeModuleFromMap(item);
                break;
              case 'array':
                this.removeArrayFromMap(item);
                break;
              case 'section':
                this.removeSectionFromMap(item);
                break;
            }
          } catch (error) {
            console.error('❌ 제거된 항목 처리 실패:', item, error);
          }
        });
      },

      /**
       * 🆕 수정된 항목 처리
       */
      handleModifiedItems(modifiedItems) {
        modifiedItems.forEach(({ old: oldItem, new: newItem }) => {
          try {
            switch (newItem.level) {
              case 'string':
                this.updateStringInMap(oldItem, newItem);
                break;
              case 'module':
                this.updateModuleInMap(oldItem, newItem);
                break;
              case 'array':
                this.updateArrayInMap(oldItem, newItem);
                break;
              case 'section':
                this.updateSectionInMap(oldItem, newItem);
                break;
            }
          } catch (error) {
            console.error('❌ 수정된 항목 처리 실패:', newItem, error);
          }
        });
      },

      /**
       * 🆕 평면화된 데이터 업데이트
       */
      async updateFlattenedData(newSiteDesign, options = {}) {
        try {
          this.flattenedSiteDesign = await flattenSiteDesignData(newSiteDesign);
          this.updateRelatedData(options);
        } catch (error) {
          console.error('❌ 평면화된 데이터 업데이트 실패:', error);
        }
      },

      /**
       * 🆕 관련 데이터 업데이트
       */
      updateRelatedData(options = {}) {
        try {
          this.calculateMaxIdsFromFlattenedData();

          if (options.rebuildTree === true) {
            this.generateTreeDataFromFlattenedData();
          }

          this.updateEntityManager();
        } catch (error) {
          console.error('❌ 관련 데이터 업데이트 실패:', error);
        }
      },

      /**
       * 🆕 Entity Manager 업데이트
       */
      updateEntityManager() {
        if (this.entityManager) {
          try {
            console.log('🔄 Entity Manager 업데이트');
            const entityData = this.buildEntityStructureFromFlattenedData();
            this.entityManager.initializeFromEntityData(entityData);
          } catch (error) {
            console.error('❌ Entity Manager 업데이트 실패:', error);
          }
        }
      },

      /**
       * 🆕 전체 데이터 새로 로드 (폴백)
       */
      /**
       * 🆕 지도 그리기 도구 관리자 초기화
       */
      initializeMapDrawingManager() {
        try {
          if (!this.map || !this.vectorSource) {
            console.warn('⚠️ 맵이나 벡터 소스가 아직 준비되지 않음');
            return;
          }
          if (this.drawingManager) {
            return;
          }
          this.drawingManager = new DrawingManager(this.map, this.vectorSource);
        } catch (error) {
          console.error('❌ DrawingManager 초기화 실패:', error);
        }
      },

      ensureDrawingManagerReady() {
        if (!this.drawingManager) {
          this.initializeMapDrawingManager();
        }
        return Boolean(this.drawingManager);
      },

      /**
       * 🆕 EntityManager 이벤트 리스너 설정
       */
      setupEntityManagerListeners() {
        if (!this.entityManager) return;

        // Entity 추가 이벤트
        this.entityManager.on('entityAdded', entity => {
          console.log('🎯 Entity 추가됨:', entity.getDisplayName());
        });

        // Entity 수정 이벤트
        this.entityManager.on('entityUpdated', entity => {
          console.log('🎯 Entity 수정됨:', entity.getDisplayName());
        });

        // Entity 삭제 이벤트
        this.entityManager.on('entityDeleted', entity => {
          console.log('🎯 Entity 삭제됨:', entity.getDisplayName());
        });

        // Entity 이동 이벤트
        this.entityManager.on('entityMoved', data => {
          console.log('🎯 Entity 이동됨:', data.entity.getDisplayName(), '->', data.newParentId);
        });

        // Entity 선택 변경 이벤트
        this.entityManager.on('entitySelectionChanged', data => {
          console.log('🎯 Entity 선택 변경:', data.entity.getDisplayName(), data.selectionType);
        });
      },

      /**
       * 🆕 Entity 계층 구조를 콘솔에 출력 (EntityManager 기반)
       */
      logEntityHierarchy() {
        if (!this.entityManager) {
          console.warn('⚠️ EntityManager가 초기화되지 않음');
          return;
        }
        this.entityManager.getRootEntities().forEach(rootEntity => {
          this.logEntityRecursive(rootEntity, 0);
        });
      },

      /**
       * 🆕 Entity를 재귀적으로 출력 (EntityManager 기반)
       */
      logEntityRecursive(entity, depth) {
        const _indent = '  '.repeat(depth);
        // console.log(`${_indent}${entity.getDisplayName()} (${entity.type})`);

        if (entity.children && entity.children.length > 0) {
          entity.children.forEach(child => {
            this.logEntityRecursive(child, depth + 1);
          });
        }
      },

      /**
       * 🆕 Entity ID로 Entity 찾기 (EntityManager 기반)
       */
      findEntityById(entityId) {
        return this.entityManager ? this.entityManager.getEntity(entityId) : null;
      },

      /**
       * 🆕 특정 타입의 Entity들 찾기 (EntityManager 기반)
       */
      findEntitiesByType(type) {
        return this.entityManager ? this.entityManager.findEntitiesByType(type) : [];
      },

      /**
       * 🆕 Entity 선택 (EntityManager 기반)
       */
      selectEntity(entityId, selectionType = 'group') {
        if (!this.entityManager) {
          console.warn('⚠️ EntityManager가 초기화되지 않음');
          return;
        }

        const entity = this.entityManager.getEntity(entityId);
        if (!entity) {
          console.warn(`⚠️ Entity를 찾을 수 없음: ${entityId}`);
          return;
        }

        console.log(`🎯 Entity 선택: ${entity.getDisplayName()} (${selectionType})`);

        // EntityManager를 통한 선택
        this.entityManager.selectEntity(entityId, 'single');

        // 기존 맵 선택 시스템과 동기화
        if (entity.mapFeature) {
          if (selectionType === 'group') {
            this.groupSelection.push(entity.mapFeature);
          } else if (selectionType === 'module') {
            this.moduleSelection.push(entity.mapFeature);
          }
        }

        // 선택된 Entity 정보 저장 (기존 시스템과 호환)
        if (selectionType === 'group') {
          this.selectedGroupFeature = {
            id: entity.id,
            name: entity.name,
            type: entity.type,
            feature: entity.mapFeature,
            solarSectionId: entity.solarSectionId,
            solarArrayId: entity.solarArrayId,
            solarStringId: entity.solarStringId,
            solarStringSeq: entity.solarStringSeq,
            solarModuleId: entity.solarModuleId,
            featureType: entity.featureType
          };
        }
      },

      /**
       * 🆕 Entity 시스템 테스트 (2단계 리팩토링 검증용)
       */
      testEntitySystem() {
        if (!this.entityManager) {
          console.error('❌ EntityManager가 초기화되지 않음');
          return;
        }

        // 1. EntityManager 상태 확인
        this.entityManager.logStatus();

        // 2. 첫 번째 String Entity 선택 테스트
        const stringEntities = this.findEntitiesByType('string');
        if (stringEntities.length > 0) {
          const firstString = stringEntities[0];
          this.selectEntity(firstString.id, 'group');
        }

        // 3. Entity 계층 구조 출력
        this.logEntityHierarchy();

        // 4. Entity 이동 테스트 (가상)
        const stringEntities2 = this.findEntitiesByType('string');
        if (stringEntities2.length > 1) {
          const sourceString = stringEntities2[0];
          const targetArray = this.findEntitiesByType('array')[0];
          // 실제 이동은 하지 않고 로그만 출력
        }
      },

      /**
       * 🆕 3단계 리팩토링 테스트 (Tree-Map 동기화 및 고급 기능)
       */
      testAdvancedFeatures() {
        if (!this.entityManager) {
          console.error('❌ EntityManager가 초기화되지 않음');
          return;
        }

        try {
          // 1. String 이동 테스트 (가상)
          const stringEntities = this.findEntitiesByType('string');
          const arrayEntities = this.findEntitiesByType('array');

          if (stringEntities.length > 0 && arrayEntities.length > 0) {
            const sourceString = stringEntities[0];
            const targetArray = arrayEntities[0];

            // 실제 이동은 하지 않고 로그만 출력
            console.log('📋 이동할 String 정보:', {
              id: sourceString.id,
              name: sourceString.name,
              childrenCount: sourceString.children?.length || 0
            });
          }

          // 2. ID 재정렬 테스트 (가상)
          if (arrayEntities.length > 0) {
            const arrayEntity = arrayEntities[0];
          }

          // 3. Tree-Map 동기화 테스트
          if (stringEntities.length > 0) {
            const testString = stringEntities[0];

            // Entity 선택 테스트
            this.syncEntitySelection(testString.id);
          }
        } catch (error) {
          console.error('❌ 3단계 테스트 중 오류:', error);
        }
      },

      /**
       * 🆕 3단계: 컨텍스트 메뉴 이벤트 처리
       * @param {Object} data - 컨텍스트 메뉴 데이터
       */
      handleContextMenu(data) {
        const { _event, item, position } = data;
        // 컨텍스트 메뉴 위치 및 아이템 설정
        this.contextMenu.x = position.x;
        this.contextMenu.y = position.y;
        this.contextMenu.item = item;
        this.contextMenu.show = true;
      },

      /**
       * String 삭제 (트리·컨텍스트 메뉴 공통) — 확인 후 API + 맵 피처 제거
       */
      async confirmDeleteStringFromTree(item, options = {}) {
        if (!item || item.type !== 'string') {
          return false;
        }

        if (!options.skipConfirm) {
          const confirmMessage = this.$t('panelPlanning.confirmDeleteStringWithModules', {
            name: item.name
          });
          if (!confirm(confirmMessage)) {
            return false;
          }
        }

        const stringSeq = this.resolveStringSeqFromTreeItem(item);
        if (stringSeq == null || stringSeq === '') {
          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'error',
              text: this.$t('panelPlanning.stringDeleteFailedMsg', {
                message: 'String ID not found'
              })
            });
          }
          return false;
        }

        const selectionToDrop = this.getMapFeaturesForTreeItem(item);

        try {
          this.isDeleting = true;
          await this.deleteSolarString(stringSeq);
          await this.deleteStringWithAllModules(stringSeq);

          this.removeStringFeaturesFromMapBySeq(stringSeq);
          this.removeSelectionHighlight();

          this.applyStringDeletedPatch?.({ stringSeq, stringNodeId: item.id });
          if (this.focusedNodeId === item.id) {
            this.focusedNodeId = null;
          }

          if (this.entityManager) {
            const entityId = this.getEntityIdFromItem(item);
            if (entityId) {
              this.entityManager.deleteEntity(entityId, true);
            }
          }

          this.selectedGroupFeature = null;
          this.selectedModuleFeature = null;
          this.removePanelMapSelectionForFeatures(selectionToDrop);

          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'success',
              text: this.$t('panelPlanning.stringDeletedSuccess', { name: item.name })
            });
          }
          return true;
        } catch (error) {
          console.error('❌ String 삭제 중 오류:', error);
          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'error',
              text: this.$t('panelPlanning.stringDeleteFailedMsg', { message: error.message })
            });
          }
          return false;
        } finally {
          this.isDeleting = false;
        }
      },

      /**
       * Module 삭제 (트리·맵 컨텍스트 메뉴 공통) — 확인 후 API + 맵 피처 제거
       */
      async confirmDeleteModuleFromTree(item, options = {}) {
        if (!item || item.type !== 'module') {
          return false;
        }

        if (!options.skipConfirm) {
          if (!confirm(this.$t('panelPlanning.confirmDeleteModule', { name: item.name }))) {
            return false;
          }
        }

        const moduleKey = resolveSolarModuleDeleteKeyFromTreeItem(item, {
          flattenedSiteDesign: this.flattenedSiteDesign
        });
        if (moduleKey == null || moduleKey === '') {
          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'error',
              text: this.$t('panelPlanning.moduleDeleteFailedMsg', {
                message: 'Module ID not found'
              })
            });
          }
          return false;
        }

        const selectionToDrop = this.getMapFeaturesForTreeItem(item);

        try {
          if (!this.selectedSite?.id) {
            throw new Error('Site not selected');
          }
          this.isDeleting = true;
          await siteDesignApi.deleteSolarModule(this.selectedSite.id, moduleKey);

          const toRemove = (this.vectorSource?.getFeatures() ?? []).filter(f =>
            isModuleMapFeatureWithSeq(f, moduleKey)
          );
          toRemove.forEach(f => {
            this.moduleSelection?.remove(f);
            this.vectorSource.removeFeature(f);
          });

          const parentString = this.findParentStringNodeForModule(item.id);
          this.applyModuleDeletedPatch?.({
            moduleSeq: moduleKey,
            moduleNodeId: item.id,
            parentStringNodeId: parentString?.id ?? null
          });
          if (this.focusedNodeId === item.id) {
            this.focusedNodeId = null;
          }

          if (this.entityManager) {
            const entityId = this.getEntityIdFromItem(item);
            if (entityId) {
              this.entityManager.deleteEntity(entityId, false);
            }
          }

          this.selectedModuleFeature = null;
          this.removePanelMapSelectionForFeatures(selectionToDrop);
          this.refreshMapSelectionStyleCache?.();
          this.removeSelectionHighlight?.();
          this.rebuildPanelMapFeatureIndex?.();
          this.requestVectorStyleRefresh?.();

          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'success',
              text: this.$t('panelPlanning.moduleDeletedSuccess', { name: item.name })
            });
          }
          return true;
        } catch (error) {
          console.error('❌ Module 삭제 중 오류:', error);
          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'error',
              text: this.$t('panelPlanning.moduleDeleteFailedMsg', { message: error.message })
            });
          }
          return false;
        } finally {
          this.isDeleting = false;
        }
      },

      /**
       * 컨텍스트 메뉴 — String / Module 삭제
       */
      async confirmDelete() {
        const item = this.contextMenu.item;
        if (!item) {
          return;
        }
        this.contextMenu.show = false;
        if (item.type === 'string') {
          await this.confirmDeleteStringFromTree(item);
        } else if (item.type === 'module') {
          await this.confirmDeleteModuleFromTree(item);
        } else if (item.type === 'array') {
          await this.confirmDeleteArrayFromTree(item);
        }
      },

      /**
       * 🆕 3단계: Entity 정보 표시
       */
      showEntityInfo() {
        if (!this.contextMenu.item) return;

        const item = this.contextMenu.item;

        // Entity 정보를 alert로 표시 (향후 모달로 개선 가능)
        alert(
          this.$t('panelPlanning.entityInfoAlert', {
            name: item.name,
            type: item.type,
            id: item.id,
            childrenCount: item.children ? item.children.length : 0
          })
        );

        // 컨텍스트 메뉴 닫기
        this.contextMenu.show = false;
      },

      /**
       * 🆕 3단계: TreeNode item에서 Entity ID 생성
       * @param {Object} item - TreeNode item
       * @returns {string} Entity ID
       */
      getEntityIdFromItem(item) {
        if (!item) return null;

        switch (item.type) {
          case 'array':
            return `array_${item.name}`;
          case 'string': {
            const seq = readStringSeqFromTreeItem(item);
            return seq != null ? `string_${seq}` : null;
          }
          case 'module':
            if (item.solarModuleSeq != null) {
              return `module_${item.solarModuleSeq}`;
            }
            if (item.seq != null) {
              return `module_${item.seq}`;
            }
            if (item.moduleId != null) {
              return `module_${item.moduleId}`;
            }
            return null;
          default:
            return null;
        }
      },

      /**
       * 맵·트리 공통 — stringSeq로 삭제 확인에 쓸 트리 노드(또는 동등 객체) resolve
       */
      resolveStringTreeItemForDelete(stringSeq) {
        if (stringSeq == null || stringSeq === '') {
          return null;
        }
        const target = String(stringSeq);
        const fromTree = this.findStringTreeItemInTreeData(this.treeData, target);
        if (fromTree) {
          return fromTree;
        }

        const stringInfo =
          this.flattenedSiteDesign?.byString?.get(target) ??
          this.flattenedSiteDesign?.byString?.get(Number(stringSeq));
        const sf = this.selectedGroupFeature;
        const displayName =
          stringInfo?.name ??
          stringInfo?.displayNo ??
          stringInfo?.stringId ??
          sf?.name ??
          readStringDisplayNo(sf) ??
          `String ${target}`;

        return {
          type: 'string',
          id: `string_${target}`,
          name: String(displayName),
          seq: Number(target) || target,
          solarStringSeq: stringSeq,
          stringSeq: stringSeq
        };
      },

      findStringTreeItemInTreeData(nodes, stringSeq) {
        const target = String(stringSeq);
        const walk = list => {
          if (!list || !Array.isArray(list)) {
            return null;
          }
          for (const node of list) {
            if (node?.type === 'string') {
              const seq = this.resolveStringSeqFromTreeItem(node);
              if (seq != null && String(seq) === target) {
                return node;
              }
            }
            const found = walk(node.children);
            if (found) {
              return found;
            }
          }
          return null;
        };
        return walk(nodes);
      },

      /** 트리 String 노드 → API DELETE 용 DB seq */
      resolveStringSeqFromTreeItem(item) {
        return resolveSolarStringSeqFromTreeItem(item, {
          flattenedSiteDesign: this.flattenedSiteDesign,
          solarStrings: this.solarStrings
        });
      },

      /** 트리 Array 노드 → PUT .../solarArray 용 DB seq (표시용 arrayId는 사용하지 않음) */
      resolveArraySeqFromTreeItem(item) {
        return resolveSolarArraySeqFromTreeItem(item, {
          flattenedSiteDesign: this.flattenedSiteDesign
        });
      },

      commitDerivedTreeData(treeData) {
        setPanelTreeLocalRegistry(treeData || []);
      },

      /** String 노드 펼침 시 lazy Module 자식을 local tree에 주입 */
      hydrateStringTreeModules(stringNodeId) {
        if (!stringNodeId) return;
        const moduleNodes = getPanelTreeModuleChildren(stringNodeId);
        if (!moduleNodes?.length) return;
        hydrateStringTreeModulesInRegistry(stringNodeId, moduleNodes);
      },

      /** Module 이동 — snapshot → 낙관적 in-place → API → 실패 시 rollback */
      buildModuleMoveSnapshot(
        sourceModuleItem,
        targetStringItem,
        numberingAssignment = null
      ) {
        const moduleSeq = resolveSolarModuleDeleteKeyFromTreeItem(sourceModuleItem, {
          flattenedSiteDesign: this.flattenedSiteDesign
        });
        const targetStringSeq = this.resolveStringSeqFromTreeItem(targetStringItem);
        const existingModule =
          this.flattenedSiteDesign?.byModule?.get(moduleSeq) ??
          this.flattenedSiteDesign?.byModule?.get(Number(moduleSeq)) ??
          this.flattenedSiteDesign?.byModule?.get(String(moduleSeq));
        const sourceStringSeq = existingModule?.parentId ?? null;
        const sourceStringNode =
          sourceStringSeq != null
            ? this.findStringTreeItemInTreeData(this.treeData, sourceStringSeq)
            : null;

        return createModuleMoveSnapshot({
          flattenedSiteDesign: this.flattenedSiteDesign,
          solarStrings: this.solarStrings,
          vectorSource: this.vectorSource,
          sourceModuleItem,
          targetStringItem,
          moduleSeq,
          targetStringSeq,
          targetStringDisplayId: readStringDisplayNoFromTreeItem(targetStringItem),
          numberingAssignment,
          sourceStringNodeId: sourceStringNode?.id ?? null,
          snapshotStringModulesState
        });
      },

      applyOptimisticModuleMoveFromSnapshot(snapshot) {
        return applyOptimisticModuleMove(
          {
            flattenedSiteDesign: this.flattenedSiteDesign,
            solarStrings: this.solarStrings,
            vectorSource: this.vectorSource
          },
          snapshot
        );
      },

      rollbackOptimisticModuleMove(snapshot) {
        rollbackModuleMove(
          {
            flattenedSiteDesign: this.flattenedSiteDesign,
            solarStrings: this.solarStrings,
            vectorSource: this.vectorSource,
            restoreStringModulesState
          },
          snapshot
        );
        this.syncFlattenedToStore?.({ force: true });
        this.rebuildPanelMapFeatureIndex?.();
        this.requestVectorStyleRefresh?.();
        this.$nextTick(() => {
          this.$refs.moduleVirtualTree?.scheduleVirtualScrollRefresh?.();
        });
      },

      async executeOptimisticModuleMove(
        sourceModuleItem,
        targetStringItem,
        numberingAssignment = null
      ) {
        const snapshot = this.buildModuleMoveSnapshot(
          sourceModuleItem,
          targetStringItem,
          numberingAssignment
        );
        if (!snapshot) {
          throw new Error(this.$t('panelPlanning.moduleMoveFailedMsg', { message: 'Snapshot failed' }));
        }

        const applied = this.applyOptimisticModuleMoveFromSnapshot(snapshot);
        if (!applied) {
          throw new Error(this.$t('panelPlanning.moduleMoveFailedMsg', { message: 'Module not found in tree' }));
        }

        this.$nextTick(() => {
          this.$refs.moduleVirtualTree?.scheduleVirtualScrollRefresh?.();
        });

        try {
          await this.handleNodeMoved({
            sourceItem: sourceModuleItem,
            targetItem: targetStringItem,
            sourceGroupName: 'module',
            targetGroupName: 'string',
            numberingAssignment
          });
          this.syncFlattenedToStore?.({ force: true });
        } catch (error) {
          this.rollbackOptimisticModuleMove(snapshot);
          throw error;
        }

        this.rebuildPanelMapFeatureIndex?.();
        this.requestVectorStyleRefresh?.();
        return { snapshot };
      },

      /** 평면화·solarStrings·맵·트리에 Array/String/Module 이름·표시 번호 반영 */
      syncEntityRenameInLocalDesign(
        entityType,
        entitySeq,
        newName,
        { treeNodeId, displayId = null } = {}
      ) {
        if (entitySeq == null || !newName || !entityType) {
          return null;
        }

        const seq = String(entitySeq);
        const parsedDisplayId = parseDisplayIdFromRename(entityType, newName);
        const displayIdStr =
          displayId != null && displayId !== ''
            ? String(displayId)
            : parsedDisplayId != null
              ? String(parsedDisplayId)
              : null;
        const field = { array: 'arrayId', string: 'stringId', module: 'moduleId' }[entityType];
        let parentScopeId = null;

        const patchRecord = record => {
          if (!record || String(record.id) !== seq) {
            return;
          }
          record.name = newName;
          if (displayIdStr && field) {
            record[field] = displayIdStr;
            if (entityType === 'string') {
              record.solarStringId = displayIdStr;
            }
            if (entityType === 'module') {
              record.solarModuleId = displayIdStr;
            }
          }
          if (entityType === 'string') {
            parentScopeId =
              parentScopeId ?? record.parentId ?? record.solarArrayId ?? null;
          } else if (entityType === 'module') {
            parentScopeId = parentScopeId ?? record.parentId ?? null;
          }
        };

        const mapKey = { array: 'byArray', string: 'byString', module: 'byModule' }[entityType];
        const info =
          this.flattenedSiteDesign?.[mapKey]?.get(Number(entitySeq)) ??
          this.flattenedSiteDesign?.[mapKey]?.get(seq);

        if (info) {
          patchRecord(info);

          if (entityType === 'string') {
            parentScopeId = parentScopeId ?? info.parentId ?? null;
            const arrayInfo = this.flattenedSiteDesign.byArray?.get(info.parentId);
            arrayInfo?.solarStrings?.forEach(patchRecord);
            arrayInfo?.children?.forEach(child => {
              if (child?.level === 'string') {
                patchRecord(child);
              }
            });
          } else if (entityType === 'module') {
            parentScopeId = parentScopeId ?? info.parentId ?? null;
            const stringInfo = this.flattenedSiteDesign.byString?.get(info.parentId);
            stringInfo?.solarModules?.forEach(patchRecord);
            stringInfo?.children?.forEach(child => {
              if (child?.level === 'module') {
                patchRecord(child);
              }
            });
          }

          const flatRow = this.flattenedSiteDesign.flattened?.find(
            item => item.level === entityType && String(item.id) === seq
          );
          if (flatRow) {
            patchRecord(flatRow);
          }
        }

        if (entityType === 'string' && Array.isArray(this.solarStrings)) {
          this.solarStrings.forEach(s => {
            if (String(s.id) === seq) {
              patchRecord(s);
            }
          });
        }

        if (entityType === 'module' && Array.isArray(this.solarStrings)) {
          this.solarStrings.forEach(s => {
            s.solarModules?.forEach(m => {
              if (String(m.id) === seq) {
                patchRecord(m);
              }
            });
          });
        }

        if (this.vectorSource) {
          this.vectorSource.getFeatures().forEach(f => {
            if (entityType === 'array') {
              if (String(f.get('solarArraySeq') ?? '') !== seq) {
                return;
              }
              f.set('solarArrayName', newName);
              return;
            }
            if (entityType === 'string') {
              if (readStringSeq(f) !== String(seq)) {
                return;
              }
              if (displayIdStr) {
                writeStringIdentityOnFeature(f, { seq, displayNo: displayIdStr });
              }
              if ((f.get('featureType') || '').toLowerCase() === 'string') {
                f.set('name', newName);
              }
              return;
            }
            if (entityType === 'module') {
              const fSeq = readModuleSeq(f);
              if (fSeq == null || String(fSeq) !== seq) {
                return;
              }
              if (displayIdStr) {
                f.set('solarModuleId', displayIdStr);
              }
              if ((f.get('featureType') || '').toLowerCase() === 'module') {
                f.set('name', newName);
              }
            }
          });
        }

        if (treeNodeId) {
          updatePanelTreeNodeEntityDisplayInPlace(treeNodeId, {
            entityType,
            newName,
            displayId: displayIdStr
          });
        }

        return parentScopeId;
      },

      /** 이름·번호 변경·삭제 후 flattened → Vuex 동기화 */
      recalculateEntityNumberingAfterChange() {
        this.syncFlattenedToStore?.({ force: true });
      },

      /** 컨텍스트 메뉴 이름 변경 후 flattened·맵·트리 로컬 동기화 */
      applyLocalRenameAfterContextMenu(item, canonicalName) {
        if (!item?.type || !canonicalName) {
          return;
        }
        const itemType = item.type;
        let seq = null;
        if (itemType === 'array') {
          seq = this.resolveArraySeqFromTreeItem(item);
        } else if (itemType === 'string') {
          seq = this.resolveStringSeqFromTreeItem(item);
        } else if (itemType === 'module') {
          seq = resolveSolarModuleDeleteKeyFromTreeItem(item, {
            flattenedSiteDesign: this.flattenedSiteDesign
          });
        }
        if (seq != null) {
          this.syncEntityRenameInLocalDesign(itemType, seq, canonicalName, {
            treeNodeId: item.id
          });
        }
        this.refreshMapLabelsAfterTreeChange?.();
      },

      async onTreeNameChanged(payload) {
        const { itemId, newName, itemType, siteId } = payload || {};
        const canonicalName = normalizeEntityRenameName(itemType, newName);
        try {
          await this.handleNameChanged({ ...payload, newName: canonicalName });
        } catch (error) {
          console.error('❌ 트리 이름 변경 실패:', error);
          this.$emit('show-message', {
            type: 'error',
            text: error?.message || this.$t('panelPlanning.renameFailedMsg', { message: '' })
          });
          return;
        }

        if (itemType === 'array' || itemType === 'string' || itemType === 'module') {
          let seq = null;
          if (itemType === 'array') {
            seq = this.resolveArraySeqFromTreeItem({
              type: 'array',
              id: itemId,
              solarArrayId: payload.solarArrayId,
              arrayId: payload.arrayId
            });
          } else if (itemType === 'string') {
            seq = this.resolveStringSeqFromTreeItem({
              type: 'string',
              id: itemId,
              solarStringId: payload.solarStringId,
              stringId: payload.stringId
            });
          } else {
            seq = resolveSolarModuleDeleteKeyFromTreeItem(
              {
                type: 'module',
                id: itemId,
                solarModuleId: payload.solarModuleId,
                moduleId: payload.moduleId
              },
              { flattenedSiteDesign: this.flattenedSiteDesign }
            );
          }
          if (seq != null) {
            this.syncEntityRenameInLocalDesign(itemType, seq, canonicalName, {
              treeNodeId: itemId
            });
          }
          this.refreshMapLabelsAfterTreeChange();
        }
      },

      refreshMapLabelsAfterTreeChange() {
        this.rebuildPanelMapFeatureIndex?.();
        this.syncModuleLabelVisibilityAfterFeaturesChange?.();
        this.map?.render();
      },

      /**
       * 🆕 String ID 순차 재정렬 (2단계 리팩토링)
       * @param {string} arrayId - 재정렬할 Array ID
       */
      reorderStringIds(arrayId) {
        if (!this.entityManager) {
          console.error('❌ EntityManager가 초기화되지 않음');
          return false;
        }

        try {
          const arrayEntity = this.entityManager.getEntity(arrayId);
          if (!arrayEntity || arrayEntity.type !== 'array') {
            console.error(`❌ Array Entity를 찾을 수 없음: ${arrayId}`);
            return false;
          }

          // String Entity들을 ID 순으로 정렬
          const stringEntities = arrayEntity.children
            .filter(child => child.type === 'string')
            .sort((a, b) => {
              const aId = parseInt(a.name) || 0;
              const bId = parseInt(b.name) || 0;
              return aId - bId;
            });

          // 새로운 순서로 재정렬
          const newOrder = stringEntities.map(entity => entity.id);
          const success = this.entityManager.reorderEntities(arrayId, newOrder);

          if (success) {
            console.log(
              '📋 새로운 순서:',
              stringEntities.map(e => e.name)
            );
            return true;
          } else {
            console.error(`❌ String ID 재정렬 실패: Array ${arrayId}`);
            return false;
          }
        } catch (error) {
          console.error('❌ String ID 재정렬 중 오류:', error);
          return false;
        }
      },

      findModuleTreeItemInTreeData(nodes, moduleId) {
        const target = String(moduleId);
        const walk = list => {
          if (!list || !Array.isArray(list)) {
            return null;
          }
          for (const node of list) {
            if (node?.type === 'module') {
              const mid = node.solarModuleId ?? node.moduleId;
              if (mid != null && String(mid) === target) {
                return node;
              }
            }
            const found = walk(node.children);
            if (found) {
              return found;
            }
          }
          return null;
        };
        return walk(nodes);
      },

      /**
       * 🆕 Entity 선택 상태 동기화 (맵-트리 양방향)
       * @param {string} entityId - 동기화할 Entity ID
       * @param {Object} item - TreeNode item (선택적으로 전달)
       */
      syncEntitySelection(entityId, item = null) {
        if (!this.entityManager || !this.vectorSource) {
          console.error('❌ EntityManager 또는 vectorSource가 초기화되지 않음');
          return false;
        }

        try {
          const entity = this.entityManager.getEntity(entityId);
          if (!entity) {
            console.error(`❌ Entity를 찾을 수 없음: ${entityId}`);
            return false;
          }

          this.entityManager.selectEntity(entityId, 'single');

          if (item) {
            return this.applyTreeItemMapSelection(item);
          }
          if (entity.type === 'string') {
            const stringSeq = entity.solarStringSeq;
            const treeNode = stringSeq != null ? this.findStringNodeByStringSeq?.(stringSeq) : null;
            return treeNode ? this.applyTreeItemMapSelection(treeNode) : false;
          }
          return false;
        } catch (error) {
          console.error('❌ Entity 선택 동기화 중 오류:', error);
          return false;
        }
      },

      /**
       * 🆕 TreeNode 포커스 이벤트 처리 (지도 동기화 포함)
       * @param {Object} data - 포커스 이벤트 데이터
       */
      handleTreeNodeFocused(data) {
        const { item } = data;
        if (!item?.id) {
          return;
        }
        const nodeId = item.id;
        this.focusedNodeId = nodeId;
        this.$nextTick(() => {
          this.$refs.moduleVirtualTree?.scrollToNodeId?.(nodeId);
        });
      },

      /**
       * 🆕 3단계: 이름 변경 요청 처리
       */
      handleRenameRequested(data) {
        const { item } = data;

        // 이름 변경 다이얼로그 표시
        this.renameDialog.item = item;
        this.renameDialog.newName = (item.name || '').replace(/^(Module|String|Array)\s+/i, '');
        this.renameDialog.show = true;
      },

      /**
       * 트리 행 삭제 버튼 — 컨텍스트 메뉴 삭제와 동일
       */
      handleDeleteRequested(data) {
        const { item } = data;
        if (!item) return;
        if (item.type === 'string') return this.confirmDeleteStringFromTree(item);
        if (item.type === 'module') return this.confirmDeleteModuleFromTree(item);
        if (item.type === 'array') return this.confirmDeleteArrayFromTree(item);
        console.warn('⚠️ 삭제 미지원 타입:', item.type);
      },

      async confirmDeleteArrayFromTree(item, options = {}) {
        if (!item || item.type !== 'array') {
          return false;
        }

        if (!options.skipConfirm) {
          if (!confirm(this.$t('panelPlanning.confirmDeleteArray', { name: item.name }))) {
            return false;
          }
        }

        let arraySeq = this.resolveArraySeqFromTreeItem(item);
        if (!arraySeq && this.flattenedSiteDesign?.byArray) {
          const found = this.flattenedSiteDesign.byArray.get(item.id);
          if (found) {
            arraySeq = found.id;
          }
        }
        if (!arraySeq) {
          const match = String(item.id || '').match(/^array_(\d+)$/);
          const parsed = match ? Number(match[1]) : null;
          if (parsed != null && parsed > 0 && parsed < 1000000000) {
            arraySeq = parsed;
          }
        }
        if (arraySeq == null || arraySeq === '') {
          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'error',
              text: this.$t('panelPlanning.arrayIdNotFound')
            });
          }
          return false;
        }

        const selectionToDrop = this.getMapFeaturesForTreeItem?.(item);

        try {
          if (!this.selectedSite?.id) {
            throw new Error('Site not selected');
          }
          this.isDeleting = true;
          await siteDesignApi.deleteSolarArray(this.selectedSite.id, arraySeq);

          this.removeArrayFeaturesFromMapBySeq(arraySeq);
          this.removeSelectionHighlight?.();

          this.applyArrayDeletedPatch?.({ arraySeq, arrayNodeId: item.id });
          if (this.focusedNodeId === item.id) {
            this.focusedNodeId = null;
          }

          if (this.entityManager) {
            const entityId = this.getEntityIdFromItem(item);
            if (entityId) {
              this.entityManager.deleteEntity(entityId, true);
            }
          }

          this.selectedGroupFeature = null;
          this.selectedModuleFeature = null;
          this.removePanelMapSelectionForFeatures?.(selectionToDrop);
          this.clearAllMapSelection?.();

          if (!options.skipConfirm) {
            this.$emit('show-message', {
              type: 'success',
              text: this.$t('panelPlanning.arrayDeletedSuccess', { name: item.name })
            });
          }
          return true;
        } catch (error) {
          console.error('Array 삭제 실패:', error);
          if (!options.skipConfirm) {
            this.$emit('show-message', { type: 'error', text: error.message });
          }
          return false;
        } finally {
          this.isDeleting = false;
        }
      },

      /**
       * 🆕 3단계: TreeNode 선택 상태 업데이트
       * @param {string} entityId - Entity ID
       * @param {boolean} selected - 선택 여부
       */
      updateTreeNodeSelection(entityId, selected) {
        // TreeNode 컴포넌트의 선택 상태를 업데이트하는 로직
        // Vue의 ref를 사용하여 직접 접근하거나 이벤트를 통해 처리
        console.log(`🔄 TreeNode 선택 상태 업데이트: ${entityId} -> ${selected}`);

        // 향후 구현: TreeNode 컴포넌트에 직접 접근하여 선택 상태 변경
      },

      /**
       * 🆕 vue-draggable-plus: 자식 배열 변경 이벤트 처리
       * @param {Object} data - 변경된 데이터
       */
      handleChildrenChanged(data) {
        const { parentItem, newChildren } = data;
        console.log('🎯 vue-draggable-plus 자식 배열 변경:', {
          parent: parentItem.name,
          newChildrenCount: newChildren.length
        });

        // 부모 아이템의 자식 배열 업데이트
        if (parentItem && parentItem.children) {
          parentItem.children = [...newChildren];

          // Vuex store에 변경사항 반영 (필요시)
          this.$emit('show-message', {
            type: 'info',
            text: this.$t('panelPlanning.treeStructureChanged', { name: parentItem.name })
          });
        }
      },

      /**
       * 🆕 vue-draggable-plus: 노드 순서 변경 이벤트 처리
       * @param {Object} data - 순서 변경 데이터
       */
      handleNodeReordered(data) {
        const { item, oldIndex, newIndex, parentItem } = data;
        console.log('🎯 vue-draggable-plus 노드 순서 변경:', {
          item: item.name,
          oldIndex,
          newIndex,
          parent: parentItem.name
        });

        // 순서 변경 로직 처리 (필요시)
        this.$emit('show-message', {
          type: 'info',
          text: this.$t('panelPlanning.treeOrderChanged', { name: item.name })
        });
      },

      /**
       * 🆕 3단계: 맵 선택을 Tree로 동기화
       * @param {Array} selectedFeatures - 선택된 피처들
       */
      syncMapSelectionToTree(selectedFeatures) {
        if (!this.entityManager || !selectedFeatures || selectedFeatures.length === 0) {
          return;
        }

        try {
          // 선택된 피처들을 Entity로 변환
          const selectedEntities = [];

          selectedFeatures.forEach(feature => {
            const entityId = feature.get('entityId');
            if (entityId) {
              const entity = this.entityManager.getEntity(entityId);
              if (entity) {
                selectedEntities.push(entity);
              }
            }
          });

          if (selectedEntities.length > 0) {
            console.log(
              '🎯 Tree 동기화할 Entity들:',
              selectedEntities.map(e => e.getDisplayName())
            );

            // Tree에서 해당 Entity들 선택 (향후 구현)
            selectedEntities.forEach(entity => {
              this.updateTreeNodeSelection(entity.id, true);
            });
          }
        } catch (error) {
          console.error('❌ 맵 선택을 Tree로 동기화 중 오류:', error);
        }
      },

      /**
       * 🆕 그리기 도구 관련 메서드들
       */

      /**
       * 🆕 그리기 모드 시작 (연속 그리기용)
       * @param {string} mode - 'rectangle' 또는 'polygon'
       */
      startDrawingMode(mode) {
        if (!this.ensureStringDrawTargetArrayForDrawing()) {
          return;
        }
        if (mode === 'rectangle') {
          this.startRectangleDrawing(false); // 연속 그리기
        } else if (mode === 'polygon') {
          this.startPolygonDrawing(false); // 연속 그리기
        }
      },

      /**
       * 🆕 그리기 모드 종료
       */
      exitDrawingMode() {
        if (this.drawingManager) {
          this.drawingManager.exitDrawingMode();
        }
        this.drawingToolbar.activeTool = null;
        this.clearLockedStringDrawTarget();
        console.log('🔄 그리기 모드 종료');
      },

      /**
       * 다각형 그리기 시작
       * @param {boolean} isSingleUse - 단발성 그리기 여부 (Context Menu용)
       */
      startPolygonDrawing(isSingleUse = false) {
        if (!this.ensureStringDrawTargetArrayForDrawing()) {
          return;
        }
        this.lockStringDrawTargetForDrawing();
        if (this.drawingToolbar.activeTool === 'polygon') {
          console.log('🔄 이미 다각형 그리기 모드입니다.');
          return;
        }

        if (!this.ensureDrawingManagerReady()) {
          console.error('❌ DrawingManager가 초기화되지 않음');
          return;
        }

        this.drawingToolbar.activeTool = 'polygon';
        this.drawingManager.setDrawingMode('polygon');

        if (isSingleUse) {
          this.drawingManager.setKeepDrawing(false);
        } else {
          this.drawingManager.setKeepDrawing(true);
        }

        this.startDrawing();
      },

      /**
       * 직사각형 그리기 시작
       * @param {boolean} isSingleUse - 단발성 그리기 여부 (Context Menu용)
       */
      startRectangleDrawing(isSingleUse = false) {
        if (!this.ensureStringDrawTargetArrayForDrawing()) {
          return;
        }
        this.lockStringDrawTargetForDrawing();
        // 🆕 Early Return 패턴으로 가독성 향상
        if (this.drawingToolbar.activeTool === 'rectangle') {
          console.log('🔄 이미 직사각형 그리기 모드입니다.');
          return;
        }

        if (!this.ensureDrawingManagerReady()) {
          console.error('❌ DrawingManager가 초기화되지 않음');
          return;
        }

        this.drawingToolbar.activeTool = 'rectangle';

        // DrawingManager에 직사각형 모드 설정
        this.drawingManager.setDrawingMode('rectangle');

        // 🆕 단발성 그리기 설정
        if (isSingleUse) {
          this.drawingManager.setKeepDrawing(false); // Context Menu: 한 번만 그리기
        } else {
          this.drawingManager.setKeepDrawing(true); // 오른쪽 상단: 계속 그리기
        }

        // 그리기 시작
        this.startDrawing();
      },

      /**
       * 🆕 맵에 String 추가
       */
      addStringToMap(stringInfo) {
        try {
          console.log('➕ 맵에 String 추가:', stringInfo.stringId);

          // 관련 정보 추출
          const arrayInfo = this.flattenedSiteDesign.byArray.get(stringInfo.parentId);
          const sectionInfo = this.flattenedSiteDesign.bySection.get(arrayInfo?.parentId);

          // String Feature 생성 및 추가
          this.drawStringPolygonWithFlattenedData(stringInfo, arrayInfo, sectionInfo);

          // solarStrings 배열에도 추가
          const solarStringData = this.convertFlattenedToStringData(
            stringInfo,
            arrayInfo,
            sectionInfo
          );
          this.solarStrings.push(solarStringData);
        } catch (error) {
          console.error('❌ String 추가 실패:', error);
        }
      },

      /**
       * 🆕 맵에 Module 추가
       */
      addModuleToMap(moduleInfo) {
        try {
          console.log('➕ 맵에 Module 추가:', moduleInfo.moduleId);

          // 관련 정보 추출
          const stringInfo = this.flattenedSiteDesign.byString.get(moduleInfo.parentId);
          const arrayInfo = this.flattenedSiteDesign.byArray.get(stringInfo?.parentId);
          const sectionInfo = this.flattenedSiteDesign.bySection.get(arrayInfo?.parentId);

          // Module Feature 생성 및 추가
          const moduleFeature = this.convToFeatureWithHierarchy(
            moduleInfo.id,
            `Module ${moduleInfo.moduleId}`,
            moduleInfo.shapeGeog.coordinates[0],
            null,
            {
              solarSectionId: sectionInfo?.id || moduleInfo.sectionIndex,
              solarArrayId: arrayInfo?.id || moduleInfo.arrayIndex,
              solarStringId: stringInfo?.stringId || moduleInfo.stringIndex,
              solarStringSeq: stringInfo?.id,
              solarModuleId: moduleInfo.moduleId,
              featureType: 'module'
            }
          );

          this.vectorSource.addFeature(moduleFeature);
          this.syncModuleLabelVisibilityAfterFeaturesChange();
        } catch (error) {
          console.error('❌ Module 추가 실패:', error);
        }
      },

      /**
       * 🆕 맵에 Array 추가
       */
      addArrayToMap(arrayInfo) {
        try {
          console.log('➕ 맵에 Array 추가:', arrayInfo.arrayId);

          // Array는 직접적인 맵 요소가 아니므로 관련 String들을 추가
          const arrayStrings = getDataByLevel(this.flattenedSiteDesign, 'string').filter(
            string => String(string.parentId) === String(arrayInfo.id)
          );

          arrayStrings.forEach(string => {
            this.addStringToMap(string);
          });
        } catch (error) {
          console.error('❌ Array 추가 실패:', error);
        }
      },

      /**
       * Array seq 기준 — 해당 Array의 String·Module polygon 전부 제거
       * @returns {import('ol/Feature').default[]}
       */
      removeArrayFeaturesFromMapBySeq(arraySeq) {
        if (!this.vectorSource || arraySeq == null || arraySeq === '') {
          return [];
        }

        const seqKey = String(arraySeq);
        const toRemove = [];
        const seen = new Set();
        const addFeature = feature => {
          if (feature && !seen.has(feature)) {
            seen.add(feature);
            toRemove.push(feature);
          }
        };

        this.vectorSource
          .getFeatures()
          .filter(f => readArraySeq(f) === seqKey)
          .forEach(addFeature);

        if (this.flattenedSiteDesign) {
          getDataByLevel(this.flattenedSiteDesign, 'string')
            .filter(string => String(string.parentId) === seqKey)
            .forEach(stringInfo => {
              const stringSeq = stringInfo.id ?? stringInfo.seq;
              if (stringSeq == null) {
                return;
              }
              const stringKey = String(stringSeq);
              this.vectorSource
                .getFeatures()
                .filter(f => readStringSeq(f) === stringKey)
                .forEach(addFeature);
              addFeature(
                this.vectorSource.getFeatureById(toPanelMapStringFeatureId(stringSeq))
              );
            });
        }

        toRemove.forEach(f => {
          this.moduleSelection?.remove(f);
          this.groupSelection?.remove(f);
          this.vectorSource.removeFeature(f);
        });
        [...(this.groupSelection?.getArray() ?? [])].forEach(f => {
          if (readArraySeq(f) === seqKey) {
            this.groupSelection.remove(f);
          }
        });

        this._cachedMapFeatureIndex = null;
        this.refreshMapSelectionStyleCache?.();
        this.rebuildPanelMapFeatureIndex?.();
        this.syncModuleLabelVisibilityAfterFeaturesChange?.();
        this.requestVectorStyleRefresh?.();
        return toRemove;
      },

      /**
       * String seq 기준 — String polygon + 하위 Module polygon 전부 제거
       * @returns {import('ol/Feature').default[]}
       */
      removeStringFeaturesFromMapBySeq(stringSeq) {
        if (!this.vectorSource || stringSeq == null || stringSeq === '') {
          return [];
        }

        const seqKey = String(stringSeq);
        const toRemove = this.vectorSource
          .getFeatures()
          .filter(f => readStringSeq(f) === seqKey);

        const stringById = this.vectorSource.getFeatureById(
          toPanelMapStringFeatureId(stringSeq)
        );
        if (stringById && !toRemove.includes(stringById)) {
          toRemove.push(stringById);
        }

        toRemove.forEach(f => {
          this.moduleSelection?.remove(f);
          this.groupSelection?.remove(f);
          this.vectorSource.removeFeature(f);
        });
        [...(this.groupSelection?.getArray() ?? [])].forEach(f => {
          if (readStringSeq(f) === seqKey) {
            this.groupSelection.remove(f);
          }
        });

        this._cachedMapFeatureIndex = null;
        this.refreshMapSelectionStyleCache?.();
        this.rebuildPanelMapFeatureIndex?.();
        this.syncModuleLabelVisibilityAfterFeaturesChange?.();
        this.requestVectorStyleRefresh?.();
        return toRemove;
      },

      /**
       * 🆕 맵에서 String 제거 (String polygon + Module + solarStrings)
       */
      removeStringFromMap(stringInfo) {
        try {
          if (!stringInfo) {
            return;
          }
          const stringSeq = stringInfo.id ?? stringInfo.seq ?? stringInfo;
          console.log('➖ 맵에서 String 제거:', stringInfo.stringId ?? stringSeq);
          this.removeStringFeaturesFromMapBySeq(stringSeq);

          const resolvedId = stringInfo.id ?? stringSeq;
          const index = (this.solarStrings || []).findIndex(
            s => String(s.id) === String(resolvedId)
          );
          if (index > -1) {
            this.solarStrings.splice(index, 1);
          }
        } catch (error) {
          console.error('❌ String 제거 실패:', error);
        }
      },

      /**
       * 🆕 맵에 Section 추가
       */
      addSectionToMap(sectionInfo) {
        try {
          console.log('➕ 맵에 Section 추가:', sectionInfo.sectionId ?? sectionInfo.id);

          const sectionArrays = getDataByLevel(this.flattenedSiteDesign, 'array').filter(
            array => String(array.parentId) === String(sectionInfo.id)
          );

          sectionArrays.forEach(array => {
            this.addArrayToMap(array);
          });
        } catch (error) {
          console.error('❌ Section 추가 실패:', error);
        }
      },

      /**
       * 🆕 맵에서 Module 제거
       */
      removeModuleFromMap(moduleInfo) {
        try {
          console.log('➖ 맵에서 Module 제거:', moduleInfo.moduleId);

          // Module Feature 제거
          const moduleFeature = this.vectorSource.getFeatureById(
            toPanelMapModuleFeatureId(moduleInfo.id)
          );
          if (moduleFeature) {
            this.vectorSource.removeFeature(moduleFeature);
          }
        } catch (error) {
          console.error('❌ Module 제거 실패:', error);
        }
      },

      /**
       * 🆕 맵에서 Array 제거
       */
      removeArrayFromMap(arrayInfo) {
        try {
          if (!arrayInfo) {
            return;
          }
          const arraySeq = arrayInfo.id ?? arrayInfo.seq ?? arrayInfo;
          console.log('➖ 맵에서 Array 제거:', arrayInfo.arrayId ?? arraySeq);
          this.removeArrayFeaturesFromMapBySeq(arraySeq);
        } catch (error) {
          console.error('❌ Array 제거 실패:', error);
        }
      },

      /**
       * 🆕 맵에서 Section 제거
       */
      removeSectionFromMap(sectionInfo) {
        try {
          console.log('➖ 맵에서 Section 제거:', sectionInfo.sectionId);

          // Section은 직접적인 맵 요소가 아니므로 관련 Array들을 제거
          const sectionArrays = getDataByLevel(this.flattenedSiteDesign, 'array').filter(
            string => string.parentId === sectionInfo.id
          );

          sectionArrays.forEach(array => {
            this.removeArrayFromMap(array);
          });
        } catch (error) {
          console.error('❌ Section 제거 실패:', error);
        }
      },

      /**
       * 🆕 맵에서 String 수정
       */
      updateStringInMap(oldString, newString) {
        try {
          console.log('✏️ 맵에서 String 수정:', newString.stringId);

          // 기존 String 제거
          this.removeStringFromMap(oldString);

          // 새로운 String 추가
          this.addStringToMap(newString);
        } catch (error) {
          console.error('❌ String 수정 실패:', error);
        }
      },

      /**
       * 🆕 맵에서 Module 수정
       */
      updateModuleInMap(oldModule, newModule) {
        try {
          console.log('✏️ 맵에서 Module 수정:', newModule.moduleId);

          // 기존 Module 제거
          this.removeModuleFromMap(oldModule);

          // 새로운 Module 추가
          this.addModuleToMap(newModule);
        } catch (error) {
          console.error('❌ Module 수정 실패:', error);
        }
      },

      /**
       * 🆕 맵에서 Array 수정
       */
      updateArrayInMap(oldArray, newArray) {
        try {
          console.log('✏️ 맵에서 Array 수정:', newArray.arrayId);

          // 기존 Array 제거
          this.removeArrayFromMap(oldArray);

          // 새로운 Array 추가
          this.addArrayToMap(newArray);
        } catch (error) {
          console.error('❌ Array 수정 실패:', error);
        }
      },

      /**
       * 🆕 맵에서 Section 수정
       */
      updateSectionInMap(oldSection, newSection) {
        try {
          console.log('✏️ 맵에서 Section 수정:', newSection.sectionId);

          // 기존 Section 제거
          this.removeSectionFromMap(oldSection);

          // 새로운 Section 추가
          this.addSectionToMap(newSection);
        } catch (error) {
          console.error('❌ Section 수정 실패:', error);
        }
      },

      /**
       * 🧪 테스트: 모듈/스트링 추가제거 및 TreeNode 연동 테스트
       */
      runIntegrationTest() {
        try {
          // 1. 현재 상태 확인
          this.logCurrentState();

          // 2. 테스트 데이터 생성
          const testData = this.createTestData();

          // 3. String 추가 테스트
          this.testStringAddition(testData.testString);

          // 4. Module 추가 테스트
          this.testModuleAddition(testData.testModule);

          // 5. String 제거 테스트
          this.testStringRemoval(testData.testString);

          // 6. Module 제거 테스트
          this.testModuleRemoval(testData.testModule);

          // 7. TreeNode 연동 테스트
          this.testTreeNodeIntegration();
        } catch (error) {
          console.error('❌ 통합 테스트 실패:', error);
        }
      },

      /**
       * 🧪 현재 상태 로깅
       */
      logCurrentState() {
        console.log('- selectedSite:', this.selectedSite?.name);
        console.log('- flattenedSiteDesign:', this.flattenedSiteDesign ? '존재' : '없음');
        console.log('- solarStrings.length:', this.solarStrings.length);

        if (this.flattenedSiteDesign) {
          const stats = getDataStatistics(this.flattenedSiteDesign);
          console.log('- 평면화된 데이터 통계:', stats);
        }
      },

      /**
       * 🧪 테스트 데이터 생성
       */
      createTestData() {
        const testString = {
          id: 'test_string_001',
          stringId: 'TEST_STRING_001',
          name: 'Test String 001',
          rowNum: 2,
          colNum: 3,
          description: '테스트용 String',
          shapeGeog: {
            coordinates: [
              [
                [127.0, 37.0],
                [127.001, 37.0],
                [127.001, 37.001],
                [127.0, 37.001],
                [127.0, 37.0]
              ]
            ]
          },
          level: 'string',
          parentId: 'test_array_001',
          sectionIndex: 0,
          arrayIndex: 0,
          stringIndex: 0,
          children: [],
          solarModules: []
        };

        const testModule = {
          id: 'test_module_001',
          moduleId: 'TEST_MODULE_001',
          shapeGeog: {
            coordinates: [
              [
                [127.0, 37.0],
                [127.0005, 37.0],
                [127.0005, 37.0005],
                [127.0, 37.0005],
                [127.0, 37.0]
              ]
            ]
          },
          centerCoord: {
            coordinates: [127.00025, 37.00025]
          },
          level: 'module',
          parentId: 'test_string_001',
          sectionIndex: 0,
          arrayIndex: 0,
          stringIndex: 0,
          moduleIndex: 0,
          children: []
        };

        return { testString, testModule };
      },

      /**
       * 🧪 String 추가 테스트
       */
      testStringAddition(testString) {
        try {
          // 1. 맵에 String 추가
          this.addStringToMap(testString);

          // 2. 상태 확인
          console.log('✅ String 추가 후 상태:');
          console.log('- solarStrings.length:', this.solarStrings.length);

          // 3. 평면화된 데이터에 추가
          if (this.flattenedSiteDesign) {
            this.flattenedSiteDesign.flattened.push(testString);
            this.flattenedSiteDesign.byString.set(testString.id, testString);
            console.log('- 평면화된 데이터에 String 추가됨');
          }
        } catch (error) {
          console.error('❌ String 추가 테스트 실패:', error);
        }
      },

      /**
       * 🧪 Module 추가 테스트
       */
      testModuleAddition(testModule) {
        try {
          // 1. 맵에 Module 추가
          this.addModuleToMap(testModule);

          // 2. 상태 확인
          console.log('✅ Module 추가 후 상태:');

          // 3. 평면화된 데이터에 추가
          if (this.flattenedSiteDesign) {
            this.flattenedSiteDesign.flattened.push(testModule);
            this.flattenedSiteDesign.byModule.set(testModule.id, testModule);
            console.log('- 평면화된 데이터에 Module 추가됨');
          }
        } catch (error) {
          console.error('❌ Module 추가 테스트 실패:', error);
        }
      },

      /**
       * 🧪 String 제거 테스트
       */
      testStringRemoval(testString) {
        try {
          // 1. 맵에서 String 제거
          this.removeStringFromMap(testString);

          // 2. 상태 확인
          console.log('✅ String 제거 후 상태:');
          console.log('- solarStrings.length:', this.solarStrings.length);

          // 3. 평면화된 데이터에서 제거
          if (this.flattenedSiteDesign) {
            const index = this.flattenedSiteDesign.flattened.findIndex(
              item => item.id === testString.id
            );
            if (index > -1) {
              this.flattenedSiteDesign.flattened.splice(index, 1);
              this.flattenedSiteDesign.byString.delete(testString.id);
              console.log('- 평면화된 데이터에서 String 제거됨');
            }
          }
        } catch (error) {
          console.error('❌ String 제거 테스트 실패:', error);
        }
      },

      /**
       * 🧪 Module 제거 테스트
       */
      testModuleRemoval(testModule) {
        try {
          // 1. 맵에서 Module 제거
          this.removeModuleFromMap(testModule);

          // 2. 상태 확인
          console.log('✅ Module 제거 후 상태:');

          // 3. 평면화된 데이터에서 제거
          if (this.flattenedSiteDesign) {
            const index = this.flattenedSiteDesign.flattened.findIndex(
              item => item.id === testModule.id
            );
            if (index > -1) {
              this.flattenedSiteDesign.flattened.splice(index, 1);
              this.flattenedSiteDesign.byModule.delete(testModule.id);
              console.log('- 평면화된 데이터에서 Module 제거됨');
            }
          }
        } catch (error) {
          console.error('❌ Module 제거 테스트 실패:', error);
        }
      },

      /**
       * 🧪 TreeNode 연동 테스트
       */
      testTreeNodeIntegration() {
        try {
          // 1. Tree 데이터 재생성
          if (this.flattenedSiteDesign) {
            this.generateTreeDataFromFlattenedData();
          }

          // 2. Entity Manager 업데이트
          if (this.entityManager) {
            this.updateEntityManager();
          }

          // 3. 최대 ID 재계산
          this.calculateMaxIdsFromFlattenedData();

          // 4. 최종 상태 확인
          this.logCurrentState();
        } catch (error) {
          console.error('❌ TreeNode 연동 테스트 실패:', error);
        }
      }
    }
  };
</script>

<style scoped>
  /* 🎯 상단 계층 선택 바 */
  .hierarchy-selection-bar {
    position: sticky;
    top: 0;
    z-index: 120;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: var(--v-surface-base, #fff);
  }

  .hierarchy-selection-bar--unset {
    background: rgba(255, 152, 0, 0.06);
  }

  .hierarchy-selection-bar__chip {
    max-width: 220px;
  }

  .hierarchy-selection-bar__chip ::v-deep .v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hierarchy-selection-bar--preview {
    background: rgba(76, 175, 80, 0.08);
    border-bottom-color: rgba(76, 175, 80, 0.35);
  }

  .hierarchy-selection-bar__preview-chip {
    max-width: 280px;
  }

  .hierarchy-selection-bar__preview-chip ::v-deep .v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* 🎯 핵심 레이아웃 스타일 */
  .page-content {
    display: flex;
    flex-direction: column;
    height: calc(100vh - var(--footerHeight) - 2 * var(--toolbarHeight));
    overflow: hidden;
  }

  .main-content {
    display: flex;
    height: 100%;
  }

  .site-sidebar {
    flex-shrink: 0;
    min-width: 200px;
    overflow: hidden;
  }

  .site-sidebar ::v-deep .drawer-edge-resize-handle {
    position: absolute;
    top: 0;
    right: -3px;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    z-index: 4;
    touch-action: none;
  }

  .site-sidebar ::v-deep .drawer-edge-resize-handle:hover,
  .site-sidebar ::v-deep .drawer-edge-resize-handle:active {
    background: rgba(0, 0, 0, 0.08);
  }

  .map-container {
    position: relative;
    display: flex;
    height: 100%;
  }

  .map-area {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 600px;
  }

  /* 🎯 Tree Panel 스타일 */
  .tree-panel {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 260px;
    height: 100%;
    background: var(--v-surface-base);
    border-left: 1px solid var(--v-secondary-base);
    z-index: 100;
    overflow: hidden;
  }

  .tree-panel ::v-deep .drawer-edge-resize-handle:hover,
  .tree-panel ::v-deep .drawer-edge-resize-handle:active {
    background: rgba(0, 0, 0, 0.08);
  }

  .tree-card {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .tree-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 8px 10px 12px;
  }

  /* 🎯 Site List 스타일 */
  .site-search-sticky {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--v-surface-base);
    flex-shrink: 0;
  }

  .site-search-input {
    font-size: 0.875rem;
  }

  .site-search-input >>> .v-input__slot {
    border-radius: 8px;
  }

  .site-list-section {
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
  }

  .site-list-item {
    cursor: pointer;
    transition: background-color var(--solvit-motion-duration) var(--solvit-motion-easing);
  }

  .site-list-item:hover {
    background-color: color-mix(in srgb, var(--v-text-base) 8%, var(--v-surface-base));
  }

  .site-list-item--selected {
    background-color: var(--solvit-row-selected-on-surface) !important;
  }

  .site-list-item--keyboard-focus:not(.site-list-item--selected) {
    background-color: color-mix(in srgb, var(--v-text-base) 6%, var(--v-surface-base));
    box-shadow: inset 3px 0 0 color-mix(in srgb, var(--v-text-base) 35%, transparent);
  }

  /* 그리기 도구 툴바 (대시보드 DotSolarMapBox 툴바와 동일한 타일·스택 규격) */
  .drawing-toolbar {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 999;
  }

  .drawing-toolbar.tree-open {
    right: calc(var(--panel-module-tree-width, 378px) + 20px);
  }

  .drawing-toolbar--map-style .drawing-toolbar-sheet {
    background-color: var(--v-surface-base) !important;
  }

  .drawing-toolbar-heading {
    color: var(--v-text-base);
    white-space: nowrap;
  }

  .drawing-toolbar--map-style ::v-deep .map-toolbar-tool-block {
    margin: 0 4px 0 0;
    vertical-align: top;
  }

  .drawing-toolbar--map-style ::v-deep .v-btn.map-toolbar-interactive-btn {
    width: 60px !important;
    min-width: 60px !important;
    max-width: 60px !important;
    height: 60px !important;
    min-height: 60px !important;
    max-height: 60px !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }

  .drawing-toolbar--map-style ::v-deep .map-toolbar-tool-btn--stack .v-btn__content {
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .drawing-toolbar--map-style ::v-deep .map-toolbar-tool-label {
    font-size: 11px;
    line-height: 1.15;
    max-width: 58px;
    text-align: center;
    word-break: break-word;
    color: var(--v-text-base);
  }

  .drawing-toolbar--map-style ::v-deep .map-toolbar-tool-btn__inner {
    width: 100%;
    max-width: 60px;
    height: 100%;
    min-height: 0;
    flex: 1 1 auto;
    justify-content: center;
    overflow: hidden;
  }

  .drawing-toolbar--map-style ::v-deep .map-toolbar-tool-btn__inner--gap-relaxed {
    gap: 0.5px !important;
  }

  .drawing-toolbar--map-style ::v-deep .map-toolbar-tool-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .drawing-toolbar--map-style ::v-deep .map-toolbar-tool-icon .v-icon.mdi {
    font-size: 22px !important;
  }

  /* 🎯 OpenLayers Popup 스타일 */
  .ol-popup {
    position: absolute;
    background-color: var(--v-surface-base);
    box-shadow: 0 1px 4px color-mix(in srgb, var(--v-text-base) 20%, transparent);
    border-radius: 10px;
    border: 1px solid var(--v-secondary-base);
    min-width: 320px;
    z-index: 1000;
    pointer-events: auto;
  }

  .ol-popup:after,
  .ol-popup:before {
    top: 100%;
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  .ol-popup:after {
    border-top-color: var(--v-surface-base);
    border-width: 10px;
    left: 48px;
    margin-left: -10px;
  }

  .ol-popup:before {
    border-top-color: var(--v-secondary-base);
    border-width: 11px;
    left: 48px;
    margin-left: -11px;
  }

  .ol-popup-closer {
    text-decoration: none;
    position: absolute;
    top: 2px;
    right: 8px;
    z-index: 1001;
  }

  .ol-popup-closer:after {
    content: '✖';
  }

  /* 🎯 Popup 내부 Vuetify 컴포넌트 스타일 */
  .ol-popup .v-card {
    box-shadow: none !important;
    border-radius: 10px;
  }

  .ol-popup .v-text-field {
    margin-bottom: 0;
  }

  .ol-popup .v-alert {
    margin-bottom: 0;
  }

  /* 책갈피 — OL Overlay 동적 DOM → ::v-deep (대시보드 DotSolarMapBox와 동일) */
  ::v-deep .map-hierarchy-bookmark-ol-overlay,
  ::v-deep .map-hierarchy-bookmark-ol-overlay * {
    pointer-events: none !important;
  }

  ::v-deep .map-hierarchy-bookmark-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    pointer-events: none;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.38));
  }

  ::v-deep .map-hierarchy-bookmark {
    --bookmark-color: #546e7a;
    position: relative;
    z-index: calc(var(--bookmark-index, 0) + 1);
    margin-top: 0;
    padding: 3px 11px 3px 11px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1.25;
    color: #fff;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    background: var(--bookmark-color);
    background:
      linear-gradient(rgba(14, 18, 24, 0.3), rgba(14, 18, 24, 0.3)),
      color-mix(in srgb, var(--bookmark-color) 82%, transparent);
    border-radius: 0 4px 4px 0;
  }

  ::v-deep .map-hierarchy-bookmark:first-child {
    border-radius: 0 4px 0 0;
    padding-top: 5px;
  }

  ::v-deep .map-hierarchy-bookmark:last-child {
    border-radius: 0 4px 4px 0;
    padding-bottom: 5px;
  }

  ::v-deep .map-hierarchy-bookmark:first-child:last-child {
    border-radius: 0 4px 4px 0;
    padding-top: 4px;
    padding-bottom: 5px;
  }

  ::v-deep .map-hierarchy-bookmark::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: color-mix(in srgb, var(--bookmark-color) 55%, #0d1117);
    border-radius: 2px 0 0 2px;
  }
</style>
